// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Point, Rectangle } from '@shared/commons/Geometry';
import { Color, Pen, PenStyle, SolidBrush } from '@shared/commons/Graphics';
import { LinkedSystem, LinkedSystemAccLinkingValue } from '../../misc/LinkedSystem';
import { AlertSubImage } from '@shared/utils/Alert/AlertConstants';
import { KeyCode, KeyEventProcessor } from '@shared/commons/KeyEventProcessor';
import { MouseButtons } from '../../UtilsClasses/ControlsUtils';
import { MathUtils } from '@shared/utils/MathUtils';
import { Resources } from '@shared/localizations/Resources';
import { Scroll } from '../Scroll';
import { QuickTableColumn, QTAlignment } from './QuickTableColumn';
import { QuickTableRow } from '../QuickTable/QuickTableRow';
import { QuickTableGroupRow } from '../QuickTable/QuickTableGroupRow';
import { QuickTableUtils } from './QuickTableUtils';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { ThemeManager } from '../../misc/ThemeManager';
import { ArrayUtils } from '@shared/utils/ArrayUtils';
import { ScrollUtils } from '../../UtilsClasses/ScrollUtils';
import { DynProperty } from '@shared/commons/DynProperty';
import { InterTraderBtnStatus } from '@shared/utils/Instruments/InterTraderBtnStatus';
import { BrowserUtils } from '@shared/commons/UtilsClasses/BrowserUtils';
import { HitTestInfo } from './WorkingModels/HitTestInfo';
import $ from 'jquery';
import { EventEmitter } from 'events';
import { BaseSettings } from '@shared/commons/Settings/BaseGeneralSettingsWrapper';
import { DisplayMode } from '@shared/utils/Enums/DisplayMode';
import { QuickTableGroupRowCalculator } from './GroupRows/QuickTableColumnAggregateCalculator';
import { QTGroupStorageItem } from './GroupRows/QTGroupStorageItem';
import { TableTotalsVisibility } from '../../cache/TableTotalsVisibility';
import { Cursors } from '@shared/commons/Cursors';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
// TODO. Use public getRowsArrayToDraw() for drawing/selection instead of rowsArray.
export class QuickTable {
    get cellLeftPadding() {
        if (isValidNumber(this._cellLeftPadding)) {
            return this._cellLeftPadding;
        }
        else {
            return BaseSettings.displayMode === DisplayMode.Compact
                ? this.COMPACT_CELL_LEFT_PADDING
                : this.REGULAR_CELL_LEFT_PADDING;
        }
    }
    set cellLeftPadding(value) {
        this._cellLeftPadding = value;
    }
    get cellRightPadding() {
        if (isValidNumber(this._cellRightPadding)) {
            return this._cellRightPadding;
        }
        else {
            return BaseSettings.displayMode === DisplayMode.Compact
                ? this.COMPACT_CELL_RIGHT_PADDING
                : this.REGULAR_CELL_RIGHT_PADDING;
        }
    }
    set cellRightPadding(value) {
        this._cellRightPadding = value;
    }
    get rowHeight() {
        if (isValidNumber(this._rowHeight)) {
            return this._rowHeight;
        }
        else {
            return ThemeManager.getMediumHeight();
        }
    }
    set rowHeight(value) {
        this._rowHeight = value;
    }
    get fontSize() {
        if (isValidNumber(this._fontSize)) {
            return this._fontSize;
        }
        else {
            return ThemeManager.getMediumFontSize();
        }
    }
    set fontSize(value) {
        this._fontSize = value;
    }
    constructor(drawingContext, isAttached) {
        this.COMPACT_CELL_LEFT_PADDING = 4;
        this.REGULAR_CELL_LEFT_PADDING = 16;
        this.COMPACT_CELL_RIGHT_PADDING = 13;
        this.REGULAR_CELL_RIGHT_PADDING = 16;
        this._rowHeight = undefined;
        this._fontSize = undefined;
        this._cellLeftPadding = undefined;
        this._cellRightPadding = undefined;
        this.needRedrawBackground = true;
        this.needRedraw = true;
        this.rowCountChanged = false;
        this.resizingNow = false;
        this.showHeader = true;
        this.isRowSeparated = false;
        this.AddToEnd = true;
        // Rows [QuickTableRow]
        this.rows = {};
        this.rowsArray = [];
        // Columns [QuickTableColumn]
        this.columns = [];
        this.sortedColumns = [];
        this.lastMouseMoveRenderer = null;
        this.lastVisibleSortedColumnIdx = -1;
        this.isMoveColumnAllowed = true;
        //
        this.width = 100;
        this.prevWidth = 100;
        this.height = 100;
        this.borderWidth = 0;
        this.separatedRowWidth = 0;
        this.separatedColumnWidth = 0;
        //
        this.verticalScrollPos = 0;
        this.selectedRowIds = [];
        this.sortIndex = -1;
        this.lockManualSorting = false;
        this.columnResizeAllowed = true;
        this.canShowHeaderMenu = true;
        this.asc = true;
        this.canUserCollapseGroups = true;
        this.useVerticalSeparatorForResizing = false;
        this.groupByColumnIndex = -1;
        this.groupStorageDict = {};
        this.movingType = QuickTableMovingType.None;
        this.activeColumnIndex = -1;
        this.lastMouseMoveHitTestInfo = null;
        this.lastWKoef = 1;
        this.headerContextMenuItems = [];
        this.tableContextMenuItems = [];
        this.groupByContextMenuItem = null;
        this.allowGroupBy = true;
        this.isReady = false;
        this.isUpdatingNow = false;
        this.ShowTotals = false;
        this.additionalFilter = false; // if true: in filtration skip items with isSubItem=true FOR FIFO POSITIONS (= displayTrades) #80010
        this.skipAccountFiltration = false;
        // public gridHorizontalVisible = false;                      // table horizontal grid visibility
        // public gridVerticalVisible = false;                        // table vertical grid visibility
        this.OnDisplayModeChanged = new CustomEvent();
        this.OnAddItem = new CustomEvent();
        this.OnDeleteItem = new CustomEvent();
        this.OnSelectionChanged = new CustomEvent();
        this.OnHoverChanged = new CustomEvent();
        this.OnVisibleColumnChanged = new CustomEvent();
        this.OnColumnResize = new CustomEvent();
        this.ColumnDisplayIndexChanged = new CustomEvent();
        this.OnCellClicked = new CustomEvent();
        this.OnDrawCell = new CustomEvent();
        this.AfterEditItem = new CustomEvent();
        this.AfterButtonItem = new CustomEvent();
        this.AfterExpandItem = new CustomEvent(); // (event for collapse FIFO pos) #80010
        this.OnPaintedPictureButtonClick = new CustomEvent();
        this.OnExternalCellEditRequest = new CustomEvent();
        // TODO. Wrong concept.
        this.OnExternalShowTooltipRequest = new CustomEvent();
        this.OnGroupsVisibilityChanged = new CustomEvent();
        this.currentCursor = Cursors.Default;
        this.beforeTableContextMenuShowing = null;
        this.lastMouseDown = new Point();
        this.columnReordering = false;
        this.mouseDownWasOnMe = false;
        this.FormatGroup = null; // function
        this.AssociatedItem = null;
        this.onTableMouseUP = new CustomEvent();
        this.onTableMouseDown = new CustomEvent();
        this.onTableMouseDoubleClick = new CustomEvent();
        this.onTableMouseMove = new CustomEvent();
        this.OnShowTooltip = new CustomEvent();
        this.CustomTextAlign = null;
        this.GroupIndexSorting = false;
        this.ColorHeaderText = null;
        this.isActiveEditControl = false;
        this.hoveredRow = null;
        this.needRefindHoveredRow = false;
        this.isVertical = false;
        this.isAttached = true;
        this.groupRowsCalculator = new QuickTableGroupRowCalculator();
        this.eventEmitter = new EventEmitter();
        this.context = drawingContext;
        this.isAttached = isAttached;
        this.lazyResizing = BrowserUtils.isIE();
        const scrollRect = this.getScrollRect();
        this.scroll = new Scroll(scrollRect.X, scrollRect.Y, scrollRect.Width, scrollRect.Height, 0, 0, this.context, this.rowHeight, isAttached);
        this.scroll.OnValueChange.Subscribe(this.OnScroll.bind(this), this);
        BaseSettings.displayModeChanged.Subscribe(this.onDisplayModeChanged, this);
    }
    get headerFont() {
        const headerFontSize = ThemeManager.getTableHeaderFontSize();
        return ThemeManager.createFont(headerFontSize);
    }
    get tableFont() {
        const size = ThemeManager.getMediumFontSize();
        return ThemeManager.createFont(size);
    }
    subscribeOnScrollElementsCountChanged(callback) {
        this.eventEmitter.on('scrollElementsCountChanged', callback);
    }
    unsubscribeOnScrollElementsCountChanged(callback) {
        this.eventEmitter.off('scrollElementsCountChanged', callback);
    }
    Dispose() {
        BaseSettings.displayModeChanged.UnSubscribe(this.onDisplayModeChanged, this);
        if (!isNullOrUndefined(this.context)) {
            $(this.context.canvas.id + '_backbuffer').remove();
        }
        this.beforeTableContextMenuShowing = null;
        this.ClearAll();
    }
    OnScroll() {
        this.needRefindHoveredRow = true;
        this.redraw();
    }
    Draw() {
        var _a, _b, _c;
        if (isNullOrUndefined(this.context))
            return;
        // Skip drawing during resize for bad performance browsers (IE)
        if (this.resizingNow && this.lazyResizing)
            return;
        const context = this.context;
        context.save();
        // all drawing settings
        context.textBaseline = 'top';
        context.imageSmoothingEnabled = true;
        const ClientW = Math.floor(this.width);
        const ClientH = Math.floor(this.height);
        this.updateLastWKoef(ClientW);
        // Полная/частичная прорисовка?
        const wasFullDrawing = this.needRedrawBackground;
        if (wasFullDrawing) {
            context.fillStyle = this.BackColor;
            context.fillRect(0, 0, ClientW, ClientH);
        }
        let hitTestInfo = null;
        if (!isNullOrUndefined(this.lastMouseMove)) {
            hitTestInfo = this.HitTest(this.lastMouseMove.X, this.lastMouseMove.Y);
        }
        let curX = 0;
        this.drawTableHeader(context, curX, hitTestInfo);
        const visibleRowsIndices = [];
        const lenCounter = this.rowsArray.length;
        for (let counter = 0; counter < lenCounter; counter++) {
            if (this.rowsArray[counter].isVisible()) {
                visibleRowsIndices.push(counter);
            }
        }
        const firstVisibleScrollIdx = this.scroll.scrollIndex >= visibleRowsIndices.length
            ? visibleRowsIndices.length - 1
            : visibleRowsIndices[this.scroll.scrollIndex];
        // Rows display rect creation.
        // TODO. Why is it here?
        let curY = this.showHeader ? this.rowHeight : this.borderWidth;
        let curTotal = 0;
        let rowK0 = null;
        if (this.ShowTotals) {
            curTotal = this.rowHeight;
            const keys = Object.keys(this.rows);
            rowK0 = this.rows[keys[0]];
        }
        let rowLen = this.rowsArray.length;
        const increaserByY = this.isRowSeparated ? this.rowHeight + this.separatedRowWidth : this.rowHeight;
        for (let ii = 0; ii < rowLen; ii++) {
            const row = this.rowsArray[ii];
            const isDisplayedRow = ii >= firstVisibleScrollIdx && curY <= ClientH - curTotal && row.isVisible();
            row.isDisplayed = isDisplayedRow;
            if (!isDisplayedRow) {
                continue;
            }
            // remember rect
            const rect = row.displayRectangle;
            rect.X = 0;
            rect.Y = curY;
            rect.Width = ClientW;
            rect.Height = this.rowHeight;
            curY += increaserByY;
        }
        if (this.needRefindHoveredRow && !isNullOrUndefined(hitTestInfo)) {
            const rowIndex = hitTestInfo.rowIndex;
            if (this.SetHoverRow(rowIndex)) {
                this.OnHoverChanged.Raise(this, rowIndex);
            }
            this.needRefindHoveredRow = false;
        }
        // Draw cell data
        context.font = ThemeManager.createFont(this.fontSize);
        const cellFontSize = this.fontSize;
        curX = 0;
        curY = 0;
        rowLen = this.rowsArray.length;
        const colLen = this.sortedColumns.length;
        const lastMouseMove = this.lastMouseMove;
        const lastMouseMoveX = !isNullOrUndefined(lastMouseMove) ? lastMouseMove.X : -1;
        const lastMouseMoveY = !isNullOrUndefined(lastMouseMove) ? lastMouseMove.Y : -1;
        const groupRowIndicesToDrawSet = {};
        const columnsTotalValues = [];
        for (let j = 0; j < colLen; j++) {
            let summTotalValue = 0;
            const curColumn = this.sortedColumns[j];
            if (!curColumn.visible || curColumn.hidden) {
                continue;
            }
            context.textAlign = this.getColumnTextAlign(curColumn);
            context.save();
            curY = this.showHeader ? this.rowHeight : this.borderWidth;
            const cellX = curX;
            let cellY = curY;
            const cellW = curColumn.PRIVATE.drawingWidth;
            const cellH = this.rowHeight;
            for (let i = 0; i < rowLen; i++) {
                const row = this.rowsArray[i];
                const cell = row.cells[curColumn.PRIVATE.index];
                if (isNullOrUndefined(cell) || !row.visible) {
                    continue;
                }
                if (this.totalCurrency == null && curColumn.totalsVisibility !== TableTotalsVisibility.None) {
                    this.filterByAccountNumber();
                }
                if (curColumn.isShowTotal()) {
                    const value = !isNullOrUndefined(row.item)
                        ? row.item.getValueForTotalCalculation(curColumn.PRIVATE.index, this.totalCurrency)
                        : cell.value;
                    if (isValidNumber(value)) {
                        summTotalValue += value;
                    }
                }
                if (curY > ClientH - curTotal || i < firstVisibleScrollIdx || !row.isVisible()) {
                    continue;
                }
                if ((!wasFullDrawing && !cell.isDirty) ||
                    // Groups are supposed to be drawn after general rows.
                    row.isGroupRow) {
                    if (row.isGroupRow) {
                        const obj = { x: cellX, w: cellW, index: curColumn.PRIVATE.index };
                        groupRowIndicesToDrawSet[i] = groupRowIndicesToDrawSet[i] || {};
                        groupRowIndicesToDrawSet[i][j] = obj;
                    }
                    curY += increaserByY;
                    continue;
                }
                // #region Cell background
                // Highlight Super Item (FIFO) #80010
                const isSuperPositionRow = this.additionalFilter &&
                    !isNullOrUndefined(row.item) &&
                    !isNullOrUndefined(row.item.superItemId) &&
                    !row.item.isSubItem;
                const rowSelected = this.selectedRowIds.includes(row.id) || isSuperPositionRow;
                const rowHovered = ((_a = this.hoveredRow) === null || _a === void 0 ? void 0 : _a.id) === row.id;
                const cellBackColor = this.getCellBackgroundColor(row, cell, rowHovered, rowSelected, i);
                const cellForeColor = this.getCellForegroundColor(row, cell, rowSelected, rowHovered);
                cellY = curY;
                let stBg = false;
                let separatedColumnWidth = this.separatedColumnWidth;
                if (this.borderWidth === 0 && cellX === 0) {
                    separatedColumnWidth = this.borderWidth;
                }
                if (!isNullOrUndefined(cell.CustomDrawingHandler)) {
                    stBg = cell.CustomDrawingHandler(context, cell, cellX + separatedColumnWidth, cellY, cellW - separatedColumnWidth, cellH);
                }
                const cellWidthForBack = cellW - separatedColumnWidth + 1;
                if (!stBg) {
                    context.fillStyle = cellBackColor;
                    context.fillRect(cellX + separatedColumnWidth, cellY, cellWidthForBack, cellH);
                }
                // Clip by cell
                context.save();
                context.beginPath();
                context.rect(cellX, cellY, cellW, cellH);
                context.clip();
                const cellRectangle = new Rectangle(cellX, cellY, cellW, cellH);
                // Controls drawing.
                if (!cell.ReadOnly && cell.QuickTableEditingInfo) {
                    // Link.
                    if (cell.QuickTableEditingInfo.ControlType === DynProperty.LINK && cell.value !== null) {
                        this.DrawLink(context, curColumn, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cellFontSize, rowSelected);
                    }
                    if (cell.QuickTableEditingInfo.ControlType === DynProperty.LINK_WITH_TEXT && cell.value !== null) {
                        this.DrawLinkWithText(context, curColumn, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cellFontSize);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.CELLBUTTON) {
                        this.DrawCellButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.BOOLEAN && cell.value !== null) {
                        this.DrawCheckBox(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cell.value);
                    }
                    else if ((cell.QuickTableEditingInfo.ControlType === DynProperty.DOUBLE ||
                        cell.QuickTableEditingInfo.ControlType === DynProperty.COMBOBOX ||
                        cell.QuickTableEditingInfo.ControlType === DynProperty.COMBOBOX_COMBOITEM_TIF ||
                        cell.QuickTableEditingInfo.ControlType === DynProperty.ACCOUNT ||
                        cell.QuickTableEditingInfo.ControlType === DynProperty.INSTRUMENT) &&
                        cell.value !== null) {
                        this.defaultReadOnlyCell(context, row, rowHovered, rowSelected, curColumn, cell, cellBackColor, cellRectangle, lastMouseMoveX, lastMouseMoveY);
                        if (cell.QuickTableEditingInfo.IsEditable) {
                            this.DrawNumeric(context, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected);
                        }
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.INFO_PICTURE &&
                        cell.value !== null) {
                        this.DrawInfoPicture(context, null, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.INFO_PICTURE_RIGHT_AND_TEXT &&
                        cell.value !== null) {
                        this.DrawInfoPictureAndTextRight(context, curColumn, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cell.value, rowSelected);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.DELAYED_PICTURE_RIGHT_AND_TEXT &&
                        cell.value !== null) {
                        // #112155
                        this.DrawDelayedPictureAndTextRight(context, curColumn, row, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cell.value, rowSelected, rowHovered, cellBackColor);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.CLOSE_BUTTON &&
                        cell.value !== null) {
                        if ((_b = row.item) === null || _b === void 0 ? void 0 : _b.Disabled) {
                            // #93127
                            this.DrawInfoPicture(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
                        }
                        else {
                            this.DrawCloseButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
                        }
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.SELL_ASSET_BUTTON) {
                        this.DrawSellAssetButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.ORDER_BUTTON &&
                        cell.value !== null) {
                        this.DrawOrderButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cell.value);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.TRADE_BUTTON &&
                        cell.value !== null) {
                        this.DrawTradeButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cell.value);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.ITCHART_ADVANCED &&
                        cell.value !== null) {
                        this.DrawITChartAdvancedButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cell.value);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.EXPAND_SUPER_POSITION_BUTTON_AND_TEXT &&
                        cell.value !== null) {
                        // (FIFO super item) #80010
                        this.DrawExpandSuperPositionButtonAndText(context, curColumn, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered, row.collapsedFIFO, cellForeColor);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.COMPONENT_OF_SUPER_POSITION_TEXT &&
                        cell.value !== null) {
                        // (FIFO sub item) #80010
                        this.DrawComponentOfSuperPositionText(context, curColumn, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cell.value, rowSelected, row);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.TRADING_CENTRAL_ITEM &&
                        cell.value !== null) {
                        this.DrawTradingCentralItem(context, curColumn, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cell.value, rowSelected, row);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.ALERT_BUTTONS_GROUP &&
                        cell.value !== null) {
                        this.DrawAlertButtonsGroup(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, row.item);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.TRUNCATE_TEXT_ADDING_ELLIPSIS &&
                        cell.value !== null) {
                        this.DrawEllipsisText(context, curColumn, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cellForeColor);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.SPARKLINE &&
                        cell.value !== null) {
                        this.DrawSparkLine(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cell.value, row.item);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.SYMBOL_WITH_IMGS &&
                        cell.value !== null) {
                        this.DrawSymbolWithImgs(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cell.value, row.item);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.PLACE_BUTTON &&
                        cell.value !== null) {
                        this.DrawPlaceButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.COLOR && cell.value !== null) {
                        this.DrawColor(context, cell, cellX, cellY, cellW, cellH);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.VISIBILITY_BUTTON &&
                        cell.value !== null) {
                        this.DrawVisibilityButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.SETTINGS_BUTTON &&
                        cell.value !== null) {
                        this.DrawSettingsButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.SHOW_ON_CHART_BUTTON &&
                        cell.value !== null) {
                        this.DrawShowOnChartButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
                    }
                    else if (cell.QuickTableEditingInfo.ControlType === DynProperty.CLOSE_VIA_OE_BUTTON) {
                        const defaultImage = ThemeManager.NewImages.closeViaOEBtnDefault;
                        const hoveredImage = ThemeManager.NewImages.closeViaOEBtnHovered;
                        const disabledImage = ThemeManager.NewImages.closeViaOEBtnDisabled;
                        const selectedImage = ThemeManager.NewImages.closeViaOEBtnSelected;
                        this.drawButton(context, defaultImage, hoveredImage, disabledImage, selectedImage, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
                    }
                }
                else if (cell.ReadOnly && cell.QuickTableEditingInfo) {
                    switch (cell.QuickTableEditingInfo.ControlType) {
                        case DynProperty.BOOLEAN:
                            break;
                        case DynProperty.SHOW_ON_CHART_BUTTON:
                            this.DrawShowOnChartButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
                            break;
                        default: {
                            this.defaultReadOnlyCell(context, row, rowHovered, rowSelected, curColumn, cell, cellBackColor, cellRectangle, lastMouseMoveX, lastMouseMoveY);
                        }
                    }
                }
                else if (!isNullOrUndefined(cell.Draw)) {
                    cell.Draw(context, cellBackColor, cellX, cellY, cellW, cellH, rowSelected, rowHovered);
                }
                else {
                    this.defaultReadOnlyCell(context, row, rowHovered, rowSelected, curColumn, cell, cellBackColor, cellRectangle, lastMouseMoveX, lastMouseMoveY);
                }
                if (((_c = row === null || row === void 0 ? void 0 : row.item) === null || _c === void 0 ? void 0 : _c.isAllowEdit(curColumn.PRIVATE.index)) === true) {
                    this.DrawNumeric(context, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected);
                }
                // reset flag
                cell.isDirty = false;
                curY += increaserByY;
                context.restore();
            }
            columnsTotalValues.push(summTotalValue);
            curX += curColumn.PRIVATE.drawingWidth;
            context.restore();
        }
        if (!isNullOrUndefined(this.hoveredRow) &&
            !this.selectedRowIds.includes(this.hoveredRow.id) &&
            !this.hoveredRow.isGroupRow) {
            const rowRectangle = this.hoveredRow.displayRectangle;
            context.strokeStyle = this.hoveredBorderColor;
            const scrollWidth = this.scroll.Visible && !this.scroll.HiddenByScrollAutohide ? Scroll.SCROLL_WIDTH : 0;
            context.strokeRect(rowRectangle.X + this.borderWidth + 0.5, rowRectangle.Y + 0.5, rowRectangle.Width - 2 * this.borderWidth - 1 - scrollWidth, rowRectangle.Height - 1); // -1 px because we add 0.5 px
        }
        this.recalculateGroupRows();
        this.DrawGroupRows(groupRowIndicesToDrawSet, wasFullDrawing, lastMouseMoveX, lastMouseMoveY);
        this.drawTotalRow(context, rowK0, columnsTotalValues);
        // Column resizing indicator
        if (this.movingType === QuickTableMovingType.ColumnResize && this.activeColumnIndex !== -1) {
            context.save();
            context.beginPath();
            context.strokeStyle = this.columnSliderColor;
            context.lineWidth = 1;
            context.setLineDash([5.5, 2.5]);
            const xCoord = this.sortedColumns[this.activeColumnIndex].PRIVATE.drawingX +
                this.sortedColumns[this.activeColumnIndex].PRIVATE.drawingWidth;
            context.moveTo(xCoord, 1);
            context.lineTo(xCoord, ClientH);
            context.stroke();
            context.restore();
        }
        context.restore();
        if (this.movingType === QuickTableMovingType.ColumnMoving && this.activeColumnIndex !== -1) {
            context.save();
            let lastMouseOverColumnIndex = -1;
            if (this.lastMouseMoveHitTestInfo != null) {
                lastMouseOverColumnIndex = this.lastMouseMoveHitTestInfo.columnIndex;
            }
            const greyP = this.dragDefaultPen;
            const p = this.dragHighlightPen;
            const curBackBrush = this.headerBackBrush;
            const activeColumn = this.sortedColumns[this.activeColumnIndex];
            context.FillRect(curBackBrush, this.lastMouseMove.X + this.verticalScrollClickDelta, 1, activeColumn.PRIVATE.drawingWidth, this.rowHeight);
            context.DrawRect(lastMouseOverColumnIndex !== this.activeColumnIndex ? p : greyP, this.lastMouseMove.X + this.verticalScrollClickDelta, 1, activeColumn.PRIVATE.drawingWidth, this.rowHeight);
            // const curF = this.headerFont;
            // if (columns[this.activeColumnIndex].HeaderFont != null)
            // curF = this.columns[this.activeColumnIndex].HeaderFont;
            const curBrush = this.headerTextColorBrush;
            // if (columns[activeColumnIndex].headerForeColorBrush != null)
            //    curBrush = _columns[activeColumnIndex].headerForeColorBrush;
            const offsetX = this.lastMouseMove.X + this.verticalScrollClickDelta + activeColumn.PRIVATE.drawingWidth / 2;
            const offsetY = this.getYPadding(this.rowHeight, QuickTableUtils.GetTextHeight(context));
            context.font = this.headerFont;
            context.DrawString(activeColumn.headerText, ThemeManager.Fonts.Font_12F_regular, curBrush, offsetX, offsetY, 'center', 'top');
            if (lastMouseOverColumnIndex !== -1 && lastMouseOverColumnIndex !== this.activeColumnIndex) {
                const lastOverColumn = this.sortedColumns[lastMouseOverColumnIndex];
                const w = lastOverColumn.PRIVATE.drawingWidth;
                const x = lastOverColumn.PRIVATE.drawingX;
                const r = w + x;
                let lineX = r;
                if (lastOverColumn.displayedIndex - activeColumn.displayedIndex === 1) {
                    lineX = r;
                }
                else if (lastOverColumn.displayedIndex - activeColumn.displayedIndex === -1) {
                    lineX = x;
                }
                else if (lastMouseOverColumnIndex > -1 && r - this.lastMouseMove.X > w / 2) {
                    lineX = x;
                }
                if (lineX === 0) {
                    lineX = 1;
                }
                context.DrawLine(p, lineX, this.rowHeight, lineX, this.height);
            }
            context.restore();
        }
        this.scroll.Draw();
        this.needRedrawBackground = this.scroll.isNeedRedrawAllLayout();
    }
    DrawGroupRows(groupRowIndicesSet, wasFullDrawing, lastMouseMoveX, lastMouseMoveY) {
        if (isNullOrUndefined(groupRowIndicesSet))
            return;
        const rowsArray = this.rowsArray;
        if (isNullOrUndefined(rowsArray))
            return;
        const context = this.context;
        const canUserCollapseGroups = this.canUserCollapseGroups;
        for (const rowIdx in groupRowIndicesSet) {
            const row = rowsArray[rowIdx];
            if (isNullOrUndefined(row) || (!row.isDirty && !wasFullDrawing)) {
                continue;
            }
            const groupTextColor = canUserCollapseGroups && row.collapsed
                ? ThemeManager.colors.tableGroupRowCollapsedForeground
                : ThemeManager.colors.tableGroupRowForeground;
            const groupRowRect = row.displayRectangle;
            let grX = groupRowRect.X;
            const grY = groupRowRect.Y;
            const grW = groupRowRect.Width;
            const grH = groupRowRect.Height;
            const rowHovered = canUserCollapseGroups && groupRowRect.Contains(lastMouseMoveX, lastMouseMoveY);
            // I'm not using context.createPattern() here as it's buggy.
            const rowColor = rowHovered ? this.tableGroupRowHoveredBrush : this.tableGroupRowDefaultBrush;
            context.FillRect(rowColor, grX, grY, grW, grH);
            grX += this.cellLeftPadding;
            if (canUserCollapseGroups) {
                const img = row.collapsed ? this.tableGroupOpenImg : this.tableGroupCloseImg;
                const imageSize = 16;
                const imgRect = new Rectangle(grX, grY, imageSize, grH);
                context.DrawCenteredImage(img, imgRect);
                const distanceBtwText = 8;
                grX += imageSize + distanceBtwText;
            }
            let fillText = row.GroupValue;
            if (!isNullOrUndefined(this.FormatGroup)) {
                fillText = this.FormatGroup(fillText);
            }
            context.textAlign = 'start';
            context.textBaseline = 'middle';
            const groupTextBrush = new SolidBrush(groupTextColor);
            QuickTableUtils.DrawTextInRect(context, fillText, this.tableFont, groupTextBrush, grX, grY, grW, grH);
            if (isValidString(row.infoIconTooltip)) {
                this.DrawInfoPicture(context, null, grX + grW - 30, grY + 3, 17, 17, lastMouseMoveX, lastMouseMoveY, false, rowHovered, row.infoIconTooltip);
            }
            let cellX = 0;
            let cellW = 0;
            let columIndex = null;
            const groupStorageItem = this.groupStorageDict[row.GroupValue];
            const groupTextEndX = QuickTableUtils.GetWidth(context, fillText) + grX;
            for (const columIdx in groupRowIndicesSet[rowIdx]) {
                const columnDataSet = groupRowIndicesSet[rowIdx][columIdx];
                cellX = columnDataSet.x;
                cellW = columnDataSet.w;
                columIndex = columnDataSet.index;
                const columValueStr = groupStorageItem.columnAggregates.get(parseInt(columIndex));
                if (isValidString(columValueStr) && cellX !== 0) {
                    context.textAlign = this.getColumnTextAlign(this.columns[columIndex]); // перенес в цикл, так как textAlign может поменяться в DrawTextInRect но это не должно влиять на последующие колонки тоталов
                    context.fillStyle = groupTextColor;
                    const xPadding = this.getXPadding(columIndex, context.textAlign);
                    if (cellX < groupTextEndX && // #87929 первая строчка более слабое условия, чтоб лишний раз не вызывать GetTextStartX TODO подумать над облегчением метода GetTextStartX имеющий схожий подсчет с DrawTextInRect
                        QuickTableUtils.GetTextStartX(context, columValueStr, cellX, grY, cellW, grH, 5, 3) <
                            groupTextEndX) {
                        continue;
                    }
                    const textBrush = new SolidBrush(groupTextColor);
                    QuickTableUtils.DrawTextInRect(context, columValueStr, this.tableFont, textBrush, cellX, grY, cellW, grH, xPadding);
                    context.FillRect(rowColor, cellX + cellW, grY, grW - cellX - cellW, grH);
                }
            }
            row.isDirty = this.ShowTotals;
        }
    }
    // #region Custom Cell Controls Drawing
    DrawLink(context, column, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cellFontSize, isSelected) {
        let foregroundColor = this.ForeColor;
        if (isSelected) {
            foregroundColor = this.selectedForeColor;
        }
        else if (QuickTable.isCellControlHovered(cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY)) {
            foregroundColor = ThemeManager.colors.LinkColor;
        }
        const textBrush = new SolidBrush(foregroundColor);
        const xPadding = this.getXPadding(column.PRIVATE.index, context.textAlign);
        const yPadding = this.getYPadding(cellH, QuickTableUtils.GetTextHeight(context));
        QuickTableUtils.DrawTextInRect(context, cell.formattedValue, this.tableFont, textBrush, cellX, cellY, cellW, cellH, xPadding, yPadding);
        if (isValidString(cell.formattedValue)) {
            const cellTextW = context.measureText(cell.formattedValue).width;
            const linkLineY = cellY + cellFontSize + yPadding;
            context.beginPath();
            context.strokeStyle = foregroundColor;
            const lineX = cellX + xPadding;
            context.moveTo(lineX, linkLineY);
            context.lineTo(lineX + cellTextW, linkLineY);
            context.stroke();
        }
    }
    DrawLinkWithText(context, column, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, cellFontSize) {
        const valueBrush = new SolidBrush(this.ForeColor);
        const xPadding = this.getXPadding(column.PRIVATE.index, context.textAlign);
        const yPadding = this.getYPadding(cellH, QuickTableUtils.GetTextHeight(context));
        QuickTableUtils.DrawTextInRect(context, cell.formattedValue, this.tableFont, valueBrush, cellX, cellY, cellW, cellH, xPadding, yPadding);
        const linkBrush = new SolidBrush(ThemeManager.colors.LinkColor);
        const cellOffset = context.measureText(cell.formattedValue).width + 15;
        const linkText = this.LinkText;
        QuickTableUtils.DrawTextInRect(context, linkText, this.tableFont, linkBrush, cellX + cellOffset, cellY, cellW, cellH, xPadding, yPadding);
        if (isValidString(cell.formattedValue) &&
            QuickTable.isCellControlHovered(cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY)) {
            // const cellTextW = context.measureText(cell.formattedValue).width;
            const linkTextW = context.measureText(linkText).width;
            const linkLineY = cellY + 4 + cellFontSize;
            context.beginPath();
            context.strokeStyle = ThemeManager.colors.LinkColor;
            context.moveTo(cellX + 5 + cellOffset, linkLineY);
            context.lineTo(cellX + 5 + linkTextW + cellOffset, linkLineY);
            context.stroke();
        }
    }
    // TODO. It's only a remove styled button for now.
    DrawCellButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY) {
        const image = QuickTable.isCellControlHovered(cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY)
            ? ThemeManager.Images.cellRemoveHoverImage
            : ThemeManager.Images.cellRemoveImage;
        context.drawImage(image, Math.round(cellX + cellW / 2 - 8) - 0.5, Math.round(cellY + cellH / 2 - 8) - 0.5);
    }
    DrawCheckBox(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, value) {
        var _a, _b;
        const btnRectangle = this.getButtonRectangle(cellX, cellY, cellW, cellH, QuickTable.CHECKBOX_SIZE);
        const hover = btnRectangle.Contains(lastMouseMoveX, lastMouseMoveY);
        const { image, backgroundBrush, borderPen } = QuickTableUtils.getCheckBoxDrawings(value, hover, (_a = cell === null || cell === void 0 ? void 0 : cell.ReadOnly) !== null && _a !== void 0 ? _a : false);
        if (hover) {
            const key = (_b = cell === null || cell === void 0 ? void 0 : cell.QuickTableEditingInfo) === null || _b === void 0 ? void 0 : _b.TooltipKey;
            if (isValidString(key)) {
                this.OnShowTooltip.Raise(key);
            }
        }
        context.RoundRectangle(btnRectangle, 2, backgroundBrush, borderPen);
        if (!isNullOrUndefined(image)) {
            context.DrawCenteredImage(image, btnRectangle, image.width, image.height);
        }
    }
    DrawNumeric(context, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, isRowSelected) {
        if (!QuickTable.isCellControlHovered(cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY)) {
            return;
        }
        const pen = isRowSelected ? this.editHighlightPenInSelectedRow : this.editHighlightPenInHoveredRow;
        const padding = pen.Width;
        const borderRadius = 4;
        const offset = 1; // An offset is needed because the cells overlap each other by 1 px
        context.SimpleRoundRectangle(cellX + padding + offset, cellY + padding, cellW - padding * 2 - offset, cellH - padding * 2, borderRadius, null, pen);
    }
    DrawInfoPicture(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered, tooltip) {
        const defaultImage = ThemeManager.NewImages.infoIconDefault;
        const hoveredImage = ThemeManager.NewImages.infoIconHovered;
        const disabledImage = ThemeManager.NewImages.infoIconDisabled;
        const selectedImage = ThemeManager.NewImages.infoIconSelected;
        this.drawButton(context, defaultImage, hoveredImage, disabledImage, selectedImage, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered, tooltip);
    }
    DrawInfoPictureAndTextRight(context, column, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, formatedValue, rowSelected) {
        var _a;
        const images = ThemeManager.Images;
        let image = null;
        const x = Math.round(cellX + cellW - 7 - 17) - 0.5;
        const y = Math.round(cellY + cellH / 2 - 7.5) - 0.5;
        // var hoverfull = QuickTable.isCellControlHovered(cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY);
        const hover = QuickTable.isCellControlHovered(x, y, 17, 17, lastMouseMoveX, lastMouseMoveY);
        image = hover ? images.i_icon_hover : images.i_icon_default;
        let foreColor = this.ForeColor;
        // Forecolor for selected row
        if (rowSelected) {
            foreColor = this.selectedForeColor;
        }
        else if (isValidString(cell.ForeColor)) {
            foreColor = cell.ForeColor;
        }
        // else if (row.ForeColor !== null) // no arg such as 'row' in this method
        //     foreColor = row.ForeColor;
        const textBrush = new SolidBrush(foreColor);
        const xPadding = this.getXPadding(column.PRIVATE.index, context.textAlign);
        const yPadding = this.getYPadding(cellH, QuickTableUtils.GetTextHeight(context));
        QuickTableUtils.DrawTextInRect(context, cell.formattedValue, this.tableFont, textBrush, cellX, cellY, cellW, cellH, xPadding, yPadding);
        // if (hoverfull)   // пока убрал, поскольку INFO_PICTURE_RIGHT_AND_TEXT нигде не используется, если потребуется где-то еще кроме MarginOEControl можно протянуть аргументом флажок отображения (по ховеру или отображать всегда)
        context.drawImage(image, x, y);
        if (hover) {
            const data = ((_a = cell.QuickTableEditingInfo) === null || _a === void 0 ? void 0 : _a.GetDataHandler)
                ? cell.QuickTableEditingInfo.GetDataHandler()
                : null;
            if (!isNullOrUndefined(data)) {
                this.OnShowTooltip.Raise(data);
            }
        }
    }
    DrawDelayedPictureAndTextRight(context, column, row, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, formatedValue, rowSelected, rowHovered, cellBackColor) {
        var _a;
        const foreColor = this.getCellForegroundColor(row, cell, rowSelected, false, column.PRIVATE.index);
        const xPadding = this.getXPadding(column.PRIVATE.index, context.textAlign);
        const btnRect = this.getButtonRectangle(cellX, cellY, cellW, cellH);
        const btnSize = btnRect.Width;
        const gap = xPadding * 2;
        const textRectW = Math.max(0, cellW - btnSize - gap);
        btnRect.X = Math.round(cellX + textRectW + xPadding);
        this.DrawEllipsisText(context, column, cell, cellX, cellY, textRectW + gap, cellH, lastMouseMoveX, lastMouseMoveY, foreColor);
        let backColor = cellBackColor;
        if (rowSelected)
            backColor = this.selectedBackColor;
        else if (rowHovered)
            backColor = this.hoveredBackColor;
        const { delayIconHovered, delayIconDefault } = ThemeManager.NewImages;
        const img = (rowSelected ? delayIconHovered : delayIconDefault).image;
        const iconX = btnRect.X;
        const iconY = Math.round(cellY + cellH / 2 - btnSize / 2);
        context.FillRect(new SolidBrush(backColor), iconX, iconY, btnSize, btnSize);
        this.DrawImage(context, cell, iconX, iconY, btnSize, btnSize, lastMouseMoveX, lastMouseMoveY, img, img);
        if (btnRect.Contains(lastMouseMoveX, lastMouseMoveY)) {
            const data = !isNullOrUndefined((_a = cell.QuickTableEditingInfo) === null || _a === void 0 ? void 0 : _a.GetDataHandler)
                ? cell.QuickTableEditingInfo.GetDataHandler()
                : null;
            if (!isNullOrUndefined(data))
                this.OnShowTooltip.Raise(data);
        }
    }
    DrawImage(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, defaultImg, hoverImg) {
        var _a;
        const imageWidth = isValidNumber(defaultImg.width) ? defaultImg.width : 17;
        const imageHeight = isValidNumber(defaultImg.height) ? defaultImg.height : 17;
        const x = Math.round(cellX + cellW / 2 - imageWidth / 2);
        const y = Math.round(cellY + cellH / 2 - imageHeight / 2);
        const hover = QuickTable.isCellControlHovered(x, y, imageWidth, imageHeight, lastMouseMoveX, lastMouseMoveY);
        const _image = hover ? hoverImg : defaultImg;
        if (hover) {
            const key = (_a = cell.QuickTableEditingInfo) === null || _a === void 0 ? void 0 : _a.TooltipKey;
            if (isValidString(key)) {
                this.OnShowTooltip.Raise(key);
            }
        }
        context.drawImage(_image, x, y);
    }
    DrawCloseButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered) {
        const defaultImage = ThemeManager.NewImages.closeBtnDefault;
        const hoveredImage = ThemeManager.NewImages.closeBtnHovered;
        const disabledImage = ThemeManager.NewImages.closeBtnDisabled;
        const selectedImage = ThemeManager.NewImages.closeBtnSelected;
        this.drawButton(context, defaultImage, hoveredImage, disabledImage, selectedImage, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
    }
    DrawVisibilityButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered) {
        let defaultImage = null;
        let hoveredImage = null;
        let selectedImage = null;
        if (cell.value) {
            defaultImage = ThemeManager.NewImages.tableVisibleDataDefault;
            hoveredImage = ThemeManager.NewImages.tableVisibleDataHovered;
            selectedImage = ThemeManager.NewImages.tableVisibleDataSelected;
        }
        else {
            defaultImage = ThemeManager.NewImages.tableInvisibleDataDefault;
            hoveredImage = ThemeManager.NewImages.tableInvisibleDataHovered;
            selectedImage = ThemeManager.NewImages.tableInvisibleDataSelected;
        }
        this.drawButton(context, defaultImage, hoveredImage, null, selectedImage, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
    }
    DrawSettingsButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered) {
        const defaultImage = ThemeManager.NewImages.settingsBtnDefault;
        const hoveredImage = ThemeManager.NewImages.settingsBtnHovered;
        const disabledImage = ThemeManager.NewImages.settingsBtnDisabled;
        const selectedImage = ThemeManager.NewImages.settingsBtnSelected;
        this.drawButton(context, defaultImage, hoveredImage, disabledImage, selectedImage, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
    }
    DrawShowOnChartButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered) {
        const defaultImage = ThemeManager.NewImages.showOnChartBtnDefault;
        const hoveredImage = ThemeManager.NewImages.showOnChartBtnHovered;
        const disabledImage = ThemeManager.NewImages.showOnChartBtnDisabled;
        const selectedImage = ThemeManager.NewImages.showOnChartBtnSelected;
        this.drawButton(context, defaultImage, hoveredImage, disabledImage, selectedImage, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
    }
    DrawPlaceButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered) {
        const defaultImage = ThemeManager.NewImages.placeBtnDefault;
        const hoveredImage = ThemeManager.NewImages.placeBtnHovered;
        const disabledImage = ThemeManager.NewImages.placeBtnDisabled;
        const selectedImage = ThemeManager.NewImages.placeBtnSelected;
        this.drawButton(context, defaultImage, hoveredImage, disabledImage, selectedImage, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
    }
    DrawSellAssetButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered) {
        const defaultImage = ThemeManager.NewImages.sellAssetBtnDefault;
        const hoveredImage = ThemeManager.NewImages.sellAssetBtnHovered;
        const disabledImage = ThemeManager.NewImages.sellAssetBtnDisabled;
        const selectedImage = ThemeManager.NewImages.sellAssetBtnSelected;
        this.drawButton(context, defaultImage, hoveredImage, disabledImage, selectedImage, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered);
    }
    DrawOrderButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, formatedValue) {
        const images = ThemeManager.Images;
        let image = null;
        const x = Math.round(cellX + cellW / 2 - 7) - 0.5;
        const y = Math.round(cellY + cellH / 2 - 7.5) - 0.5;
        const hover = QuickTable.isCellControlHovered(x, y, 17, 17, lastMouseMoveX, lastMouseMoveY);
        if (formatedValue.status === InterTraderBtnStatus.Open) {
            image = hover ? images.order_hover : images.order_default;
        }
        else {
            image = images.order_closed;
        }
        if (hover && formatedValue.tooltip) {
            this.OnShowTooltip.Raise(formatedValue.tooltip);
        } // пока так, потом исправим общим механизмом тултипов в таблицах
        context.drawImage(image, x, y);
    }
    DrawTradeButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, formatedValue) {
        const images = ThemeManager.Images;
        let image = null;
        const x = Math.round(cellX + cellW / 2 - 7) - 0.5;
        const y = Math.round(cellY + cellH / 2 - 7.5) - 0.5;
        const hover = QuickTable.isCellControlHovered(x, y, 17, 17, lastMouseMoveX, lastMouseMoveY);
        if (formatedValue.status === InterTraderBtnStatus.Open) {
            image = hover ? images.trade_hover : images.trade_default;
        }
        else {
            image = images.trade_closed;
        }
        if (hover && formatedValue.tooltip) {
            this.OnShowTooltip.Raise(formatedValue.tooltip);
        } // пока так, потом исправим общим механизмом тултипов в таблицах
        context.drawImage(image, x, y);
    }
    DrawITChartAdvancedButton(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, formatedValue) {
        const images = ThemeManager.Images;
        this.DrawImage(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, images.advanced_chart_default, images.advanced_chart_hover);
    }
    /* TODO: Remove, as it appears to be deprecated. */
    DrawAlertButtonsGroup(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, alertItem) {
        const scrollWidth = this.scroll.Visible ? 4 : 0;
        const x1 = Math.round(cellX + cellW - 64) - 0.5 - scrollWidth;
        const x2 = x1 + 20;
        const x3 = x2 + 20;
        const y = Math.round(cellY + cellH / 2 - 7.5) - 0.5;
        const isHoveredPlayImage = QuickTable.isCellControlHovered(x1, y, 17, 17, lastMouseMoveX, lastMouseMoveY);
        const isHoveredUpdateImage = QuickTable.isCellControlHovered(x2, y, 17, 17, lastMouseMoveX, lastMouseMoveY);
        const isHoveredCloseImage = QuickTable.isCellControlHovered(x3, y, 17, 17, lastMouseMoveX, lastMouseMoveY);
        const playImage = alertItem.GetImage(AlertSubImage.PLAY_STOP, isHoveredPlayImage);
        const updateImage = alertItem.GetImage(AlertSubImage.UPDATE, isHoveredUpdateImage);
        const closeImage = alertItem.GetImage(AlertSubImage.CLOSE, isHoveredCloseImage);
        context.drawImage(playImage, x1, y);
        context.drawImage(updateImage, x2, y);
        context.drawImage(closeImage, x3, y);
    }
    DrawEllipsisText(context, column, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, foreColor) {
        const text = cell.formattedValue;
        const textBrush = new SolidBrush(foreColor);
        const xPadding = this.getXPadding(column.PRIVATE.index, context.textAlign);
        const yPadding = this.getYPadding(cellH, QuickTableUtils.GetTextHeight(context));
        QuickTableUtils.DrawTextInRect(context, text, this.tableFont, textBrush, cellX, cellY, cellW, cellH, xPadding, yPadding, true);
        const isHover = QuickTable.isCellControlHovered(cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY);
        if (isHover &&
            QuickTableUtils.GetWidth(context, text) > cellW - xPadding &&
            isValidString(cell.QuickTableEditingInfo.TooltipKey)) {
            this.OnShowTooltip.Raise(cell.QuickTableEditingInfo.TooltipKey);
        }
    }
    DrawExpandSuperPositionButtonAndText(context, column, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered, isClollapsed, cellForeColor) {
        const xPadding = this.getXPadding(column.PRIVATE.index, context.textAlign);
        const yPadding = this.getYPadding(cellH, QuickTableUtils.GetTextHeight(context));
        let offsetX = cellX;
        if (this.additionalFilter) {
            const expandCollapseButtonRectangle = this.getButtonRectangle(cellX, cellY, cellW, cellH);
            expandCollapseButtonRectangle.X = cellX + xPadding;
            let defaultImage;
            let hoveredImage;
            let disabledImage;
            let selectedImage;
            if (isClollapsed) {
                defaultImage = ThemeManager.NewImages.superPositionCollapseDefault;
                hoveredImage = ThemeManager.NewImages.superPositionCollapseHovered;
                disabledImage = ThemeManager.NewImages.superPositionCollapseDisabled;
                selectedImage = ThemeManager.NewImages.superPositionCollapseSelected;
            }
            else {
                defaultImage = ThemeManager.NewImages.superPositionExpandDefault;
                hoveredImage = ThemeManager.NewImages.superPositionExpandHovered;
                disabledImage = ThemeManager.NewImages.superPositionExpandDisabled;
                selectedImage = ThemeManager.NewImages.superPositionExpandSelected;
            }
            const style = this.getButtonStyle(defaultImage, hoveredImage, disabledImage, selectedImage, rowSelected, rowHovered, cell, expandCollapseButtonRectangle, lastMouseMoveX, lastMouseMoveY);
            this.drawButtonStyle(context, expandCollapseButtonRectangle, style);
            offsetX = expandCollapseButtonRectangle.Right();
            cellW = cellW - offsetX;
        }
        const textBrush = new SolidBrush(cellForeColor);
        QuickTableUtils.DrawTextInRect(context, cell.formattedValue, this.tableFont, textBrush, offsetX, cellY, cellW, cellH, xPadding, yPadding);
    }
    DrawComponentOfSuperPositionText(context, column, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, formatedValue, rowSelected, row) {
        var _a;
        const offsetX = 19;
        // const offsetY = 5;
        let foreColor = this.ForeColor;
        // Forecolor for selected row
        if (rowSelected) {
            foreColor = this.selectedForeColor;
        }
        else if (cell.ForeColor) {
            foreColor = cell.ForeColor;
        }
        else if (row.ForeColor !== null) {
            foreColor = row.ForeColor;
        }
        if ((_a = row === null || row === void 0 ? void 0 : row.item) === null || _a === void 0 ? void 0 : _a.Disabled) {
            // #93127
            foreColor = ThemeManager.colors.qtDisabledRowTextColor;
        }
        const textBrush = new SolidBrush(foreColor);
        const xPadding = this.getXPadding(column.PRIVATE.index, context.textAlign);
        const yPadding = this.getYPadding(cellH, QuickTableUtils.GetTextHeight(context));
        QuickTableUtils.DrawTextInRect(context, cell.formattedValue, this.tableFont, textBrush, cellX + offsetX, cellY + 2, cellW, cellH, xPadding, yPadding);
    }
    DrawTradingCentralItem(context, column, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, formatedValue, rowSelected, row) {
        const theme = ThemeManager.colors;
        const item = row.item;
        const valueName = item.valueName;
        const value = item.value;
        const offsetX = 5;
        let x = cellX + offsetX;
        let y = cellY + 2;
        const nameBrush = new SolidBrush(theme.accentColor);
        const xPadding = this.getXPadding(column.PRIVATE.index, context.textAlign);
        const yPadding = this.getYPadding(cellH, QuickTableUtils.GetTextHeight(context));
        if (item.valueNameVisible) {
            // if is not line break row
            QuickTableUtils.DrawTextInRect(context, valueName, this.tableFont, nameBrush, x, y, cellW, cellH, xPadding, yPadding);
        }
        x += item.offsetByValueName;
        const imgName = item.imgName;
        if (imgName) {
            const img = theme[imgName];
            y += img.height / 2 + (imgName === 'neutral' ? 4.5 : 1); // image alignment
            context.drawImage(img, x + img.width, y);
        }
        else {
            const valueBrush = new SolidBrush(this.ForeColor);
            QuickTableUtils.DrawTextInRect(context, value, this.tableFont, valueBrush, x, y, cellW, cellH, xPadding, yPadding);
        }
    }
    DrawSparkLine(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, formatedValue, item) {
        const arr = formatedValue;
        const len = Math.min(item.sparkLinePointNum, arr.length);
        const startI = arr.length - len;
        const backupOldStrokeStyle = context.strokeStyle; // сохраним стиль пера чтобы не вызывать ресурсоёмкие save/restore
        context.strokeStyle = cell.ForeColor || Color.White;
        context.beginPath(); // Начинает новый путь
        const offsetX = 8;
        const dx = (cellW - offsetX * 2) / (len - 1);
        let x = cellX + offsetX;
        for (let i = 0; i < len; i++) {
            const y = cellY + 0.5 * cellH * (1 - arr[i + startI] / 100); // допустимые значения от -100% до 100%
            context[i ? 'lineTo' : 'moveTo'](x, y); // если нулевая точка, то передвигаем перо в начальную точку, иначе рисуем линию до очередной точки
            x += dx;
        }
        context.stroke(); // Отображает путь
        context.strokeStyle = backupOldStrokeStyle; // возвращаем прежний стиль пера
    }
    DrawSymbolWithImgs(context, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, formatedValue, item) {
        const theme = ThemeManager.colors;
        const circleOffsetX = 7;
        const circleR = 4;
        let offsetX = 21;
        const offsetY = 5;
        const backupOldStrokeStyle = context.strokeStyle; // сохраним стиль пера чтобы не вызывать ресурсоёмкие save/restore
        context.strokeStyle = item.insColor; // готовим к отрисовке кружочка
        context.fillStyle = item.insColor;
        context.beginPath();
        context.arc(cellX + circleOffsetX + circleR, cellY + cellH / 2, circleR, 0, 2 * Math.PI, false);
        context.fill(); // Отображает круг
        context.strokeStyle = backupOldStrokeStyle; // возвращаем прежний стиль пера
        if (item.showLogos) {
            const img = item.logoIMG;
            if (img) {
                context.drawImage(img, cellX + offsetX, cellY + offsetY);
                offsetX += img.width + 5;
            }
        }
        const textBrush = new SolidBrush(theme.accentColor);
        QuickTableUtils.DrawTextInRect(context, formatedValue, this.tableFont, textBrush, cellX + offsetX, cellY + offsetY, cellW, cellH);
    }
    DrawColor(context, cell, cellX, cellY, cellW, cellH) {
        const lineWidth = Math.max(1, cellW - 10);
        const xOffset = Math.max(1, cellW - lineWidth);
        const yOffset = cellH / 2 - 3;
        const linePen = new Pen(cell.value, 3);
        context.DrawLine(linePen, cellX + xOffset, cellY + yOffset, cellX + cellW - xOffset, cellY + yOffset);
    }
    // QuickTable.isCellImageHovered  (
    //    cellX, cellY, cellW, cellH,
    //    lastMouseMoveX, lastMouseMoveY)
    // {
    //    return cellX <= lastMouseMoveX && lastMouseMoveX < cellX + cellW &&
    //        cellY <= lastMouseMoveY && lastMouseMoveY < cellY + cellH;
    // };
    static isCellControlHovered(cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY) {
        return (cellX <= lastMouseMoveX &&
            lastMouseMoveX < cellX + cellW &&
            cellY <= lastMouseMoveY &&
            lastMouseMoveY < cellY + cellH);
    }
    // #endregion
    setShowColumnHeaders(show) {
        if (this.showHeader === show) {
            return;
        }
        this.showHeader = show;
        this.scroll.setBounds(this.getScrollRect());
    }
    onResizeBegin() {
        this.resizingNow = true; /* Skip drawing during resize */
        this.redraw();
    }
    onResizeEnd() {
        this.resizingNow = false;
        this.redraw();
    }
    // Sort columns
    UpdateSortedColumns() {
        for (let i = 0; i < this.columns.length; i++) {
            this.columns[i].PRIVATE.index = i;
        }
        this.sortedColumns = [];
        this.sortedColumns = this.sortedColumns.concat(this.columns);
        this.sortedColumns.sort(QuickTable.updateSortedColumnsComparator);
        this.updateLastVisibleSortedColumnIdx();
    }
    updateLastVisibleSortedColumnIdx() {
        let lastVisibleSortedColumnIdx = -1;
        const sortedColumns = this.sortedColumns;
        let i = sortedColumns.length;
        while (i--) {
            const column = sortedColumns[i];
            if (!column.visible || column.hidden) {
                continue;
            }
            lastVisibleSortedColumnIdx = i;
            break;
        }
        this.lastVisibleSortedColumnIdx = lastVisibleSortedColumnIdx;
    }
    setAllRowsVisibility(visible) {
        const flag = this.additionalFilter;
        this.rowsArray.forEach(function (row) {
            var _a;
            if (!((_a = row.item) === null || _a === void 0 ? void 0 : _a.isSubItem) || flag) {
                row.visible = visible;
                row.collapsedFIFO = true;
            }
        });
        this.rowCountChanged = true;
        this.needRedrawBackground = true;
    }
    filterFIFOByAccountNumber(row, collapsed, flag) {
        if (row.item.superItemId) {
            if (row.item.isSubItem && (!collapsed || !flag)) {
                // sub item && needUpdate
                row.visible = false;
            }
            else {
                // apply visible change
                collapsed = row.collapsedFIFO;
            } // remember supers' state
        }
        return collapsed;
    }
    filterByAccountNumber(acctNumber = null) {
        if (acctNumber == null && LinkedSystem.accLinkingActive) {
            acctNumber = LinkedSystem.getAccount();
        }
        const flag = this.additionalFilter;
        let collapsed = true;
        this.totalCurrency = null;
        this.rowsArray.forEach((row) => {
            const item = row.item;
            if (item != null) {
                const rowAcc = item.GetCurrentAccount();
                if (rowAcc != null) {
                    row.visible = rowAcc.AcctNumber === acctNumber || acctNumber == null; // update row visibility if filter by account exist
                }
                if (row.isVisible()) {
                    collapsed = this.filterFIFOByAccountNumber(row, collapsed, flag);
                    this.totalCurrency = item.updateTotalCurrencyWith(this.totalCurrency); // обновляем валюту отображаемую в тотале табл. #110253
                }
            }
        });
        this.updateGroupRowVisibility();
        this.rowCountChanged = true;
        this.needRedrawBackground = true;
    }
    updateGroupRowVisibility() {
        Object.values(this.groupStorageDict).forEach((groupItem) => {
            const hasVisibleRow = groupItem.groupRowsArray.some((row) => row.visible);
            groupItem.groupHeaderRow.visible = hasVisibleRow;
        });
    }
    visibleRowCount() {
        let num = 0;
        this.rowsArray.forEach(function (row) {
            if (row.isVisible() && !row.isGroupRow) {
                num++;
            }
        });
        return num;
    }
    static updateSortedColumnsComparator(col1, col2) {
        const dispIdx1 = col1.displayedIndex;
        const dispIdx2 = col2.displayedIndex;
        if (dispIdx1 === dispIdx2) {
            return col1.sortIndex > col2.sortIndex ? 1 : -1;
        }
        else {
            return dispIdx1 > dispIdx2 ? 1 : -1;
        }
    }
    ClearAll() {
        this.ClearRows();
    }
    ClearRows() {
        this.rowsArray.forEach(function (row) {
            row.Dispose();
        });
        this.rows = {};
        this.rowsArray = [];
        this.selectedRowIds = [];
        this.afterSelectionChanged(true);
        this.clearAllGroupData();
        this.updateScrollElementsCount();
        this.rowCountChanged = true;
    }
    BeginUpdate() {
        this.isUpdatingNow = true;
    }
    EndUpdate() {
        this.isUpdatingNow = false;
        this.endUpdateWork();
    }
    // TODO. Rename.
    endUpdateWork() {
        if (this.groupByColumnIndex !== -1) {
            this.recreateRowsArrayFromGroupStorage();
        }
        this.updateScrollElementsCount();
        this.needRedrawBackground = true;
    }
    AddRow(row) {
        if (!isNullOrUndefined(this.rows[row.id])) {
            return;
        }
        this.rows[row.id] = row;
        if (this.AddToEnd) {
            this.rowsArray.push(row);
        }
        else {
            this.rowsArray.splice(0, 0, row);
        }
        if (this.groupByColumnIndex !== -1) {
            this.addRowToGroupStorage(row);
        }
        // В режиме апдейта не делаем лишний раз
        if (!this.isUpdatingNow) {
            this.endUpdateWork();
        }
        this.totalCurrency = null;
        this.rowCountChanged = true;
        return row;
    }
    InsertRow(row, insertIdx) {
        if (!isNullOrUndefined(this.rows[row.id])) {
            return;
        }
        this.rows[row.id] = row;
        this.rowsArray.splice(insertIdx, 0, row);
        if (this.groupByColumnIndex !== -1) {
            this.addRowToGroupStorage(row);
        }
        // В режиме апдейта не делаем лишний раз
        if (!this.isUpdatingNow) {
            this.endUpdateWork();
        }
        this.totalCurrency = null;
        this.rowCountChanged = true;
    }
    RemoveRow(rowId) {
        const row = this.rows[rowId];
        if (!row)
            return;
        this.rowsArray.splice($.inArray(row, this.rowsArray), 1);
        delete this.rows[rowId];
        const idx = this.selectedRowIds.indexOf(rowId);
        if (idx !== -1) {
            this.selectedRowIds.splice(idx, 1);
            this.afterSelectionChanged(true);
        }
        if (this.groupByColumnIndex !== -1) {
            this.removeRowFromGroupStorage(row);
        }
        row.Dispose();
        // В режиме апдейта не делаем лишний раз
        if (!this.isUpdatingNow) {
            this.endUpdateWork();
        }
        this.totalCurrency = null;
        this.rowCountChanged = true;
    }
    updateRowsCellsValues(forceUpdate) {
        const updatedRowsToRegroupDataArray = [];
        const len = this.rowsArray.length;
        for (let i = 0; i < len; i++) {
            const row = this.rowsArray[i];
            // TODO
            if (isNullOrUndefined(row === null || row === void 0 ? void 0 : row.item)) {
                continue;
            }
            // TODO. refactor. Optimize.
            const oldGroupName = this.GetGroupKey(row);
            row.FillRowByItem(row.item, forceUpdate);
            row.isDirty = true;
            const newGroupName = this.GetGroupKey(row);
            if (newGroupName !== oldGroupName) {
                updatedRowsToRegroupDataArray.push({
                    row,
                    oldGroupName
                });
            }
        }
        this.regroupUpdatedRows(updatedRowsToRegroupDataArray);
    }
    sortRowsArray() {
        if (this.groupByColumnIndex !== -1) {
            this.recreateRowsArrayFromGroupStorage();
        }
        else {
            this.rowsArray.sort(this.plainRowsArrayComparator.bind(this));
        }
        this.regroupSuperItems(); // (FIFO) #80010
        this.redraw();
    }
    plainRowsArrayComparator(a, b) {
        let cell1Value = a.id;
        let cell2Value = b.id;
        if (this.sortIndex !== -1) {
            cell1Value = a.cells[this.sortIndex].value;
            cell2Value = b.cells[this.sortIndex].value;
        }
        if (!isValidValue(cell1Value) && isValidValue(cell2Value))
            return 1;
        if (isValidValue(cell1Value) && !isValidValue(cell2Value))
            return -1;
        if (!isValidValue(cell1Value) && !isValidValue(cell2Value))
            return 0;
        let res = 0;
        if (cell1Value < cell2Value) {
            res = -1;
        }
        else if (cell1Value > cell2Value) {
            res = 1;
        }
        if (!this.asc) {
            res *= -1;
        }
        return res;
    }
    // TODO
    InitializeDirect(item) {
        const newColumns = [];
        const len = item.ColumnCount();
        this.AssociatedItem = item;
        for (let i = 0; i < len; i++) {
            const newColumn = new QuickTableColumn();
            // Todo need share
            const par = item.GetColumnParams(i);
            newColumn.beginGroup = par.BeginGroup;
            newColumn.MenuIndex = par.MenuIndex;
            newColumn.headerText = par.HeaderText();
            newColumn.headerKey = par.HeaderKey;
            newColumn.FormulaKey = par.FormulaKey;
            newColumn.width = par.Width;
            newColumn.visible = par.Visible;
            newColumn.visibleDefault = par.Visible;
            newColumn.hidden = par.Hidden || Resources.isHidden(par.HeaderKey);
            newColumn.ToolTipHeaderText = par.ToolTip;
            newColumn.totalsVisibility = par.aggregateVisibility;
            newColumn.EmptyValue = par.EmptyValue;
            newColumn.maxFilterIndex = par.MaxFilterIndex;
            newColumn.displayedIndex = i;
            newColumn.allowGrouping = par.AllowGrouping;
            newColumn.AutoGenerated = par.AutoGenerated;
            newColumn.alignment = par.alignment;
            newColumns.push(newColumn);
        }
        this.columns = newColumns;
        this.populateHeaderContextMenu(newColumns);
        this.createGroupByContextMenuItem(newColumns);
    }
    AddItem(item) {
        if (!isNullOrUndefined(this.rows[item.ItemId])) {
            this.rows[item.ItemId].FillRowByItem(item);
            this.rows[item.ItemId].isDirty = true;
            return null;
        }
        const row = this.AddRow(new QuickTableRow(item, this));
        if (this.sortIndex !== -1) {
            this.sortRowsArray();
        }
        this.OnAddItem.Raise(row);
        return row;
    }
    getItemById(key) {
        const res = this.rows[key];
        return res || null;
    }
    getTableItemsIds() {
        return Object.keys(this.rows);
    }
    getSelectedTableItems() {
        const selectedItems = [];
        for (let i = 0; i < this.selectedRowIds.length; i++) {
            const row = this.getItemById(this.selectedRowIds[i]);
            if (!isNullOrUndefined(row === null || row === void 0 ? void 0 : row.item)) {
                selectedItems.push(row.item);
            }
        }
        return selectedItems;
    }
    getSelectedRowsIndexes() {
        const selectedRowIndexes = [];
        for (let i = 0; i < this.selectedRowIds.length; i++) {
            const row = this.getItemById(this.selectedRowIds[i]);
            selectedRowIndexes.push(this.rowsArray.indexOf(row));
        }
        return selectedRowIndexes;
    }
    setSelectedRowsByIndexes(indexes) {
        const selectedRowIds = [];
        for (let i = 0; i < indexes.length; i++) {
            const index = indexes[i];
            selectedRowIds.push(this.rowsArray[index].id);
        }
        this.selectedRowIds = selectedRowIds;
        this.redraw();
    }
    RemoveItem(id) {
        this.RemoveRow(id);
        this.OnDeleteItem.Raise(id);
    }
    // TODO.
    RemoveSelectedItems() {
        const selectedRowIds = this.selectedRowIds;
        while (selectedRowIds.length > 0) {
            this.RemoveRow(selectedRowIds[0]);
        }
    }
    //
    onMouseDown(event) {
        if (this.movingType === QuickTableMovingType.ColumnMoving && event.button == MouseButtons.Middle) {
            return;
        }
        this.columnReordering = false;
        // Row selection
        this.lastMouseDown = new Point(event.offsetX, event.offsetY);
        const hitTestInfo = this.HitTest(event.offsetX, event.offsetY);
        this.movingType = QuickTableMovingType.None;
        this.activeColumnIndex = hitTestInfo.columnIndex;
        // Click by scroll
        if (hitTestInfo.zone === QuickTableZone.VerticalScroll) {
            this.movingType = QuickTableMovingType.VerticalScroll;
            this.scroll.mouseDown(event);
        }
        else if (hitTestInfo.zone === QuickTableZone.Header) {
            /* Click by header */
            /*
                it's empty here for now
            */
        }
        else if (hitTestInfo.zone === QuickTableZone.HeaderGrid ||
            hitTestInfo.zone === QuickTableZone.VerticalResizeSeparator) {
            /* Click on header grid - start resize columns */
            if (hitTestInfo.columnIndex !== -1 &&
                !hitTestInfo.lastVisibleSortedColumn &&
                this.columnResizeAllowed &&
                !this.isActiveEditControl) {
                this.movingType = QuickTableMovingType.ColumnResize;
                this.activeColumnIndex = hitTestInfo.columnIndex;
            }
        }
        else if (event.button === MouseButtons.Left) {
            /* Click by row */
            const ctrl = event.ctrlKey;
            // todo support SHIFT
            if (!ctrl)
                this.selectedRowIds = [];
            if (hitTestInfo.rowIndex !== -1) {
                const row = this.rowsArray[hitTestInfo.rowIndex];
                if (row.isGroupRow) {
                    this.processGroupRowMouseDown(row);
                    return;
                }
                const clickedRowIdx = this.selectedRowIds.indexOf(row.id);
                if (clickedRowIdx === -1) {
                    this.selectedRowIds.push(row.id);
                }
                else if (ctrl) {
                    this.selectedRowIds.splice(clickedRowIdx, 1);
                }
            }
            if (hitTestInfo.columnIndex !== -1) {
                const columnIndex = this.sortedColumns[hitTestInfo.columnIndex].PRIVATE.index;
                this.OnCellClicked.Raise(this, columnIndex, hitTestInfo.rowIndex);
            }
            this.needRedrawBackground = true;
            this.OnSelectionChanged.Raise(/* fromMouseClick */ true, this);
        }
        // Refresh
        this.needRedraw = true;
        this.mouseDownWasOnMe = true;
        this.onTableMouseDown.Raise(hitTestInfo);
    }
    onContextMenu(event) {
        const hitTestInfo = this.HitTest(event.offsetX, event.offsetY);
        if (hitTestInfo.zone === QuickTableZone.Header && this.canShowHeaderMenu) {
            showContextMenu({ x: event.clientX, y: event.clientY, items: this.headerContextMenuItems });
            return true;
        }
        else if (hitTestInfo.zone === QuickTableZone.Table) {
            if (!isNullOrUndefined(this.beforeTableContextMenuShowing)) {
                this.beforeTableContextMenuShowing(this);
            }
            const items = this.createTableContextMenuItems();
            event.stopPropagation();
            showContextMenu({ x: event.clientX, y: event.clientY, items });
            return true;
        }
        return false;
    }
    onMouseClick(event) {
        if (event.button === MouseButtons.Middle) {
            return false;
        }
        const hitTestInfo = this.HitTest(event.offsetX, event.offsetY);
        if (hitTestInfo.rowIndex !== -1 && hitTestInfo.columnIndex !== -1) {
            const cellIndex = this.sortedColumns[hitTestInfo.columnIndex].PRIVATE.index;
            const cell = this.rowsArray[hitTestInfo.rowIndex].cells[cellIndex];
            if (!isNullOrUndefined(cell) && !cell.ReadOnly && !isNullOrUndefined(cell.QuickTableEditingInfo)) {
                this.BeginEdit(cell.QuickTableEditingInfo, cell, this.rowsArray[hitTestInfo.rowIndex], hitTestInfo.columnIndex, hitTestInfo.realColumnIndex, event);
                return true;
            }
        }
        return false;
    }
    onMouseMove(event) {
        const hitTestInfo = this.HitTest(event.offsetX, event.offsetY);
        this.scroll.onMouseMove(event);
        // Column reordering
        // выполняется только если смещение мышки было больше значения mouseOffset
        if (event.button == MouseButtons.Left /* && AllowColumnsReordering */ &&
            !this.columnReordering &&
            (Math.abs(this.lastMouseDown.X - event.offsetX) > QuickTable.MouseOffset ||
                Math.abs(this.lastMouseDown.Y - event.offsetY) > QuickTable.MouseOffset) /* && !ReadOnly */ &&
            hitTestInfo.zone === QuickTableZone.Header &&
            this.movingType === QuickTableMovingType.None &&
            event.buttons !== 0 &&
            this.mouseDownWasOnMe &&
            this.isMoveColumnAllowed) {
            this.columnReordering = true;
            this.movingType = QuickTableMovingType.ColumnMoving;
            this.activeColumnIndex = hitTestInfo.columnIndex;
            if (this.activeColumnIndex !== -1) {
                this.verticalScrollClickDelta =
                    this.sortedColumns[this.activeColumnIndex].PRIVATE.drawingX - this.lastMouseDown.X;
            }
        }
        this.updateCursor(hitTestInfo);
        // Click by scroll
        if (this.movingType === QuickTableMovingType.VerticalScroll) {
            this.scroll.mouseMove(event);
        }
        else if (this.movingType === QuickTableMovingType.ColumnResize) {
            /* Resizing columns */
            const activeColumnIndex = this.activeColumnIndex;
            const lastMouseMoveHitTestInfo = this.lastMouseMoveHitTestInfo;
            if (activeColumnIndex !== -1 &&
                !isNullOrUndefined(lastMouseMoveHitTestInfo) &&
                lastMouseMoveHitTestInfo.x !== -1) {
                const delta = Math.round(hitTestInfo.x - lastMouseMoveHitTestInfo.x);
                const change = delta / this.lastWKoef;
                const activeColumn = this.sortedColumns[activeColumnIndex];
                let newW = change + activeColumn.width;
                if (newW < 7) {
                    newW = 7;
                }
                else {
                    newW -= this.CorrectColumnsWidth(change);
                }
                if (activeColumn.width !== newW) {
                    activeColumn.width = newW;
                    this.redraw();
                    this.OnColumnResize.Raise();
                }
            }
        }
        else if (this.movingType === QuickTableMovingType.ColumnMoving && this.activeColumnIndex !== -1) {
            this.needRedrawBackground = true;
        }
        else {
            const rowIndex = hitTestInfo.rowIndex;
            if (this.SetHoverRow(rowIndex)) {
                this.OnHoverChanged.Raise(this, rowIndex);
            }
        }
        let isTooltipHandled = false;
        if (hitTestInfo.columnIndex >= 0 && hitTestInfo.rowIndex >= 0) {
            const row = this.rowsArray[hitTestInfo.rowIndex];
            const cell = this.rowsArray[hitTestInfo.rowIndex].cells[hitTestInfo.realColumnIndex];
            const item = row.item;
            const column = this.sortedColumns[hitTestInfo.columnIndex];
            const columnID = column.PRIVATE.index;
            if (item === null || item === void 0 ? void 0 : item.isAllowEdit(columnID)) {
                this.redraw();
            }
            else if (!isNullOrUndefined(column.renderer)) {
                if (!isNullOrUndefined(this.lastMouseMoveRenderer) && this.lastMouseMoveRenderer !== column.renderer) {
                    this.processMouseLeaveColumnRenderer();
                }
                this.lastMouseMoveRenderer = column.renderer;
                const eventArgs = new QuickTableMouseMoveEventArgs(this, row, hitTestInfo.columnIndex, cell, hitTestInfo.x, hitTestInfo.y);
                column.renderer.ProcessMouseMove(eventArgs);
                isTooltipHandled = eventArgs.isTooltipHandled;
                if (eventArgs.needRedraw) {
                    this.redraw();
                }
            }
        }
        else {
            this.processMouseLeaveColumnRenderer();
        }
        // TODO. Wrong concept, wrong place.
        // Showing tooltip.
        if (hitTestInfo.zone === QuickTableZone.Header && this.movingType === QuickTableMovingType.None) {
            const column = this.columns[hitTestInfo.columnIndex];
            const ttText = column ? column.tooltipKey : null;
            this.OnExternalShowTooltipRequest.Raise(ttText, hitTestInfo.columnIndex);
        }
        else if (hitTestInfo.zone === QuickTableZone.Table &&
            hitTestInfo.rowIndex !== -1 &&
            hitTestInfo.realColumnIndex !== -1) {
            const cell = this.rowsArray[hitTestInfo.rowIndex].cells[hitTestInfo.realColumnIndex];
            if (!isTooltipHandled) {
                if (cell.CanShowTooltip()) {
                    this.OnExternalShowTooltipRequest.Raise(cell.GetTooltipKey(), hitTestInfo.rowIndex, hitTestInfo.realColumnIndex);
                }
                else {
                    // Hiding tooltip.
                    this.OnExternalShowTooltipRequest.Raise(null);
                }
            }
        }
        else {
            // Hiding tooltip.
            this.OnExternalShowTooltipRequest.Raise(null);
        }
        this.processGroupHitTestInfo(hitTestInfo, this.lastMouseMoveHitTestInfo);
        this.processCellControlHitTestInfo(hitTestInfo, this.lastMouseMoveHitTestInfo);
        this.lastMouseMove = new Point(event.offsetX, event.offsetY);
        this.lastMouseMoveHitTestInfo = hitTestInfo;
        this.onTableMouseMove.Raise(hitTestInfo);
    }
    onMouseLeave(event) {
        // TODO. Wrong concept, wrong place.
        // Hiding tooltip.
        this.OnExternalShowTooltipRequest.Raise(null);
        const hitTestInfo = this.HitTest(-1, -1);
        this.processGroupHitTestInfo(hitTestInfo, this.lastMouseMoveHitTestInfo);
        this.processCellControlHitTestInfo(hitTestInfo, this.lastMouseMoveHitTestInfo);
        this.updateCursor(hitTestInfo);
        this.lastMouseMove = new Point(-1, -1);
        this.lastMouseMoveHitTestInfo = hitTestInfo;
        if (this.SetHoverRow(-1)) {
            this.OnHoverChanged.Raise(this, -1);
        }
        this.processMouseLeaveColumnRenderer();
        this.redraw();
    }
    processMouseLeaveColumnRenderer() {
        if (!isNullOrUndefined(this.lastMouseMoveRenderer)) {
            const eventArgs = new QuickTableMouseEventArgs(this);
            this.lastMouseMoveRenderer.ProcessMouseLeave(eventArgs);
            this.lastMouseMoveRenderer = null;
            if (eventArgs.needRedraw) {
                this.redraw();
            }
        }
    }
    SetHoverRow(rowIndex) {
        var _a;
        const isRemoveHover = rowIndex === -1;
        if (isRemoveHover) {
            if (isNullOrUndefined(this.hoveredRow)) {
                return false;
            }
            this.hoveredRow = null;
            this.redraw();
            return true;
        }
        else {
            const row = this.rowsArray[rowIndex];
            if ((row === null || row === void 0 ? void 0 : row.id) !== ((_a = this.hoveredRow) === null || _a === void 0 ? void 0 : _a.id)) {
                this.hoveredRow = row;
                this.redraw();
                return true;
            }
            return false;
        }
    }
    // #region Mostly Grouping stuff
    // Collapses/expands a group of rows.
    processGroupRowMouseDown(groupRow) {
        if (!this.canUserCollapseGroups || !(groupRow === null || groupRow === void 0 ? void 0 : groupRow.isGroupRow)) {
            return;
        }
        this.updateGroupCollapsedState(groupRow.GroupValue, !groupRow.collapsed);
    }
    setGroupCollapsedState(groupRow, collapsed) {
        if (!(groupRow === null || groupRow === void 0 ? void 0 : groupRow.isGroupRow)) {
            return;
        }
        this.updateGroupCollapsedState(groupRow.GroupValue, collapsed);
    }
    updateGroupCollapsedState(group, collapsed) {
        const groupStorageItem = this.groupStorageDict[group];
        if (isNullOrUndefined(groupStorageItem)) {
            return;
        }
        groupStorageItem.groupHeaderRow.collapsed = collapsed;
        let needUpdate = false;
        const visible = !collapsed;
        const groupRowsDict = groupStorageItem.groupRowsDict;
        for (const groupRowKey in groupRowsDict) {
            const row = groupRowsDict[groupRowKey];
            needUpdate = row.isRowInCollapsedGroup !== collapsed || needUpdate;
            row.isRowInCollapsedGroup = collapsed;
            if (visible) {
                this.correctGroupRowVisibility(row);
            } // account linking and fifo trades visibility correction
        }
        if (!needUpdate) {
            return;
        }
        this.OnGroupsVisibilityChanged.Raise();
        this.updateScrollElementsCount();
        this.redraw();
    }
    setGroupByColumnIdx(idx) {
        if (this.groupByColumnIndex === idx) {
            return;
        }
        this.groupByColumnIndex = idx;
        if (this.groupByColumnIndex === -1) {
            this.clearAllGroupData();
            this.sortRowsArray();
        }
        else {
            this.regroupAll();
        }
        this.regroupSuperItems(); // (FIFO) #80010
        this.updateGroupRowVisibility();
        this.redraw();
    }
    /**
     * Regroups all items while preserving the collapse state of each group.
     * Same as regroupAll() but with the restoration of previous collapsed states.
     */
    regroupAllWithSameCollapseState() {
        const backUpCollapsedStates = {};
        for (const group in this.groupStorageDict) {
            const { groupHeaderRow } = this.groupStorageDict[group];
            backUpCollapsedStates[group] = groupHeaderRow.collapsed;
        }
        this.regroupAll();
        for (const group in this.groupStorageDict) {
            const { groupHeaderRow } = this.groupStorageDict[group];
            this.setGroupCollapsedState(groupHeaderRow, !!backUpCollapsedStates[group]);
        }
    }
    regroupAll() {
        this.clearAllGroupData();
        const rowsArray = this.rowsArray;
        const len = rowsArray.length;
        for (let i = 0; i < len; i++) {
            this.addRowToGroupStorage(rowsArray[i]);
        }
        this.recreateRowsArrayFromGroupStorage();
    }
    recreateRowsArrayFromGroupStorage(skipSortingOfRowsInGroups = false, sortByIndex = false) {
        const groupStorageDict = this.groupStorageDict;
        if (isNullOrUndefined(groupStorageDict))
            return;
        const groupOrderArray = Object.keys(groupStorageDict);
        let sortMethod = this.groupNameComparator.bind(this);
        if (sortByIndex) {
            sortMethod = this.groupHeaderRowSortIndexComparator.bind(this);
        }
        if (this.GroupIndexSorting) {
            sortMethod = this.groupRowSortGroupIndexComparator.bind(this);
        }
        groupOrderArray.sort(sortMethod);
        const plainRowsArrayComparator = sortByIndex
            ? QuickTable.groupRowsSortIndexComparator
            : this.plainRowsArrayComparator.bind(this);
        let newRowsArray = [];
        const len = groupOrderArray.length;
        for (let i = 0; i < len; i++) {
            const groupStorageItem = groupStorageDict[groupOrderArray[i]];
            newRowsArray.push(groupStorageItem.groupHeaderRow);
            const groupRowsArray = groupStorageItem.groupRowsArray;
            if (!skipSortingOfRowsInGroups) {
                groupRowsArray.sort(plainRowsArrayComparator);
            }
            newRowsArray = newRowsArray.concat(groupRowsArray);
        }
        this.rowsArray = newRowsArray;
        this.regroupSuperItems();
        this.updateScrollElementsCount();
    }
    clearAllGroupData() {
        const groupStorageDict = this.groupStorageDict;
        this.groupStorageDict = {};
        const rowsArray = this.rowsArray;
        let needUpdateScrollElementsCount = false;
        for (const groupName in groupStorageDict) {
            const groupStorageItem = groupStorageDict[groupName];
            const groupRowsDict = groupStorageItem ? groupStorageItem.groupRowsDict : null;
            if (!groupRowsDict)
                continue;
            // Restoring rows visibility.                       // комментарии ниже могут пригодится если прилетит тикет по поводу того, что FIFO позиция не запоминает своего collapse состояния, когда убирается Group By
            // let collapseState = {}, laterStorage = []
            for (const groupRowKey in groupRowsDict) {
                const row = groupRowsDict[groupRowKey];
                row.isRowInCollapsedGroup = false;
                needUpdateScrollElementsCount = true;
                this.correctGroupRowVisibility(row); // account linking and fifo trades visibility correction
                /* if (row.item && row.item.superItemId)         // All this commented rows are for restore collapseFIFO state
            {
                if (!this.additionalFilter && row.item.isSubItem)
                    row.visible = false

                if (this.additionalFilter)
                {
                    if (row.item.isSubItem)    // change state of sub items now or later
                    {
                        if (collapseState.hasOwnProperty(row.item.superItemId))
                            row.visible = collapseState[row.item.superItemId]
                        else
                            laterStorage.push(row)
                    }
                    else
                        collapseState[row.item.ItemId] = row.collapsedFIFO  // remember collapse state of super item
                }
                else
                    if (row.item.isSubItem)
                        row.visible = false         // hide sub items if display trades off
            } */
            }
            // for (let i = 0; i < laterStorage.length; i++)       // for sub items that was earlier than super
            //    laterStorage[i].visible = collapseState[laterStorage[i].item.superItemId]
            // Removing group header rows.
            const idx = rowsArray.indexOf(groupStorageItem.groupHeaderRow);
            if (idx !== -1) {
                rowsArray.splice(idx, 1);
                needUpdateScrollElementsCount = true;
            }
        }
        // if (LinkedSystem.accLinkingActive)      // restore account filtration
        //     this.filterByAccountNumber(LinkedSystem.accountStorage[LinkedSystemAccLinkingValue])
        if (needUpdateScrollElementsCount) {
            this.updateScrollElementsCount();
        }
    }
    addRowToGroupStorage(row, forceAddToGroupKey, sortIndexForGroup) {
        var _a;
        if (isNullOrUndefined(row))
            return;
        const groupStorageDict = this.groupStorageDict;
        if (isNullOrUndefined(groupStorageDict))
            return;
        const groupName = isNullOrUndefined(forceAddToGroupKey) ? this.GetGroupKey(row) : forceAddToGroupKey;
        let groupStorageItem = null;
        if (groupStorageDict.hasOwnProperty(groupName)) {
            groupStorageItem = groupStorageDict[groupName];
        }
        else {
            groupStorageItem = new QTGroupStorageItem(this.columns, this.groupRowsCalculator, this.groupByColumnIndex);
            const groupHeaderRow = new QuickTableGroupRow(this);
            groupHeaderRow.GroupValue = groupName;
            groupHeaderRow.sortIndex = sortIndexForGroup;
            groupStorageItem.groupHeaderRow = groupHeaderRow;
            if (!isNullOrUndefined((_a = row.item) === null || _a === void 0 ? void 0 : _a.GroupIndex)) {
                groupStorageItem.GroupIndex = row.item.GroupIndex;
            }
            groupStorageDict[groupName] = groupStorageItem;
        }
        const groupRowsDict = groupStorageItem.groupRowsDict;
        if (groupRowsDict[row.id])
            return;
        groupRowsDict[row.id] = row;
        const groupRowsArray = groupStorageItem.groupRowsArray;
        if (this.AddToEnd) {
            groupRowsArray.push(row);
        }
        else {
            groupRowsArray.splice(0, 0, row);
        }
        // Sync row visibility.
        row.isRowInCollapsedGroup = groupStorageItem.groupHeaderRow.collapsed;
        if (row.visible) {
            this.correctGroupRowVisibility(row);
        } // account linking and fifo trades visibility correction
    }
    removeRowFromGroupStorage(row, forceRemoveFromGroupKey) {
        if (isNullOrUndefined(row))
            return;
        const groupStorageDict = this.groupStorageDict;
        if (isNullOrUndefined(groupStorageDict))
            return;
        const groupName = isNullOrUndefined(forceRemoveFromGroupKey) ? this.GetGroupKey(row) : forceRemoveFromGroupKey;
        const groupStorageItem = groupStorageDict[groupName];
        if (isNullOrUndefined(groupStorageItem))
            return;
        const groupRowsDict = groupStorageItem.groupRowsDict;
        if (isNullOrUndefined(groupRowsDict[row.id]))
            return;
        delete groupRowsDict[row.id];
        const groupRowsArray = groupStorageItem.groupRowsArray;
        const idx = groupRowsArray.indexOf(row);
        if (idx === -1)
            return;
        groupRowsArray.splice(idx, 1);
        if (!groupRowsArray.length) {
            delete groupStorageDict[groupName];
        }
        // Restoring row visibility.
        row.isRowInCollapsedGroup = false;
        this.correctGroupRowVisibility(row); // account linking and fifo trades visibility correction
    }
    regroupUpdatedRows(updatedRowsToRegroupDataArray) {
        if (isNullOrUndefined(updatedRowsToRegroupDataArray)) {
            return;
        }
        const len = updatedRowsToRegroupDataArray.length;
        for (let i = 0; i < len; i++) {
            const item = updatedRowsToRegroupDataArray[i];
            const oldGroupName = item.oldGroupName;
            const row = item.row;
            this.removeRowFromGroupStorage(row, oldGroupName);
            this.addRowToGroupStorage(row);
        }
        if (len) {
            this.recreateRowsArrayFromGroupStorage(/* skipSortingOfRowsInGroups */ true);
        }
    }
    correctGroupRowVisibility(row) {
        // fix visibility when Group By + (AccountLinking or FIFO)
        if (isNullOrUndefined(row.item) || !row.visible) {
            return;
        }
        const ls = LinkedSystem;
        const acc = row.item.GetCurrentAccount();
        if (row.item.superItemId && !row.collapsedFIFO && !row.item.isSubItem) {
            // super item with uncollapsed row
            row.collapsedFIFO = true;
        } // collapse state
        if ((!this.skipAccountFiltration &&
            ls.accLinkingActive &&
            acc &&
            acc.BstrAccount != ls.accountStorage[LinkedSystemAccLinkingValue]) || // Account Linking On + Group Open Bug Fix
            (!this.additionalFilter && row.item.isSubItem)) {
            // Not display trades + Group Open Bug Fix
            row.visible = false;
        }
    }
    regroupSuperItems() {
        var _a;
        // first show super item -> then sub items (restore this order here after populating or sorting)
        const rows = this.rowsArray;
        const regroupedRows = [];
        const subRowsStorage = {};
        for (let i = 0; i < rows.length; i++) {
            const item = rows[i].item;
            if (item === null || item === void 0 ? void 0 : item.isSubItem) {
                // sub item
                if (!isNullOrUndefined(subRowsStorage[item.superItemId])) {
                    subRowsStorage[item.superItemId].push(rows[i]);
                }
                else {
                    subRowsStorage[item.superItemId] = [rows[i]];
                }
            }
            else {
                /* first add only super or other items */
                regroupedRows.push(rows[i]);
            }
        }
        const superKeys = Object.keys(subRowsStorage); // sub items left checking
        if (superKeys.length === 0) {
            // no sub items
            return;
        }
        let newRows = [];
        for (let i = 0; i < regroupedRows.length; i++) {
            const row = regroupedRows[i];
            newRows.push(row);
            if ((_a = row.item) === null || _a === void 0 ? void 0 : _a.superItemId) {
                // super item
                const subRow = subRowsStorage[row.item.ItemId];
                if (!isNullOrUndefined(subRow)) {
                    newRows.splice.apply(newRows, [newRows.length, 0].concat(subRow)); // insert array from subRowsStorage (with sub items of current super item) inside newRows array
                    subRowsStorage[row.item.ItemId] = []; // clear storage for sub items left checking
                }
            }
        }
        for (let i = 0; i < superKeys.length; i++) {
            if (subRowsStorage[superKeys[i]].length > 0) {
                // if subs left => there is no super for exist sub yet
                newRows = newRows.concat(subRowsStorage[superKeys[i]]);
            }
        } // just add to the end
        this.rowsArray = newRows;
    }
    groupHeaderRowSortIndexComparator(groupKey1, groupKey2) {
        const groupStorageDict = this.groupStorageDict;
        const groupHeaderRow1 = groupStorageDict[groupKey1].groupHeaderRow;
        const groupHeaderRow2 = groupStorageDict[groupKey2].groupHeaderRow;
        return groupHeaderRow1.sortIndex - groupHeaderRow2.sortIndex;
    }
    groupRowSortGroupIndexComparator(groupKey1, groupKey2) {
        const groupStorageDict = this.groupStorageDict;
        const groupRow1 = groupStorageDict[groupKey1];
        const groupRow2 = groupStorageDict[groupKey2];
        if (!groupRow1.GroupIndex || !groupRow2.GroupIndex) {
            return this.groupNameComparator(groupKey1, groupKey2);
        }
        return groupRow1.GroupIndex - groupRow2.GroupIndex;
    }
    static groupRowsSortIndexComparator(row1, row2) {
        return row1.sortIndex - row2.sortIndex;
    }
    groupNameComparator(groupKey1, groupKey2) {
        if (this.sortIndex === -1 || this.groupByColumnIndex !== this.sortIndex || this.asc) {
            return groupKey1.localeCompare(groupKey2);
        }
        return groupKey2.localeCompare(groupKey1);
    }
    getSubItemsRowsCount(countOnlyVisible) {
        var _a;
        // (FIFO) #80010 count sub items number for panel header (countOnlyVisible == false -> also count invisible items)
        let count = 0;
        for (let i = 0; i < this.rowsArray.length; i++) {
            const row = this.rowsArray[i];
            if (row && (row.isVisible() || !countOnlyVisible) && ((_a = row.item) === null || _a === void 0 ? void 0 : _a.isSubItem)) {
                count++;
            }
        }
        return count;
    }
    // Group header rows are excluded.
    getDataRowsCount() {
        if (this.groupByColumnIndex === -1) {
            return this.rowsArray.length;
        }
        let count = 0;
        const groupStorageDict = this.groupStorageDict;
        for (const groupKey in groupStorageDict) {
            count += groupStorageDict[groupKey].groupRowsArray.length;
        }
        return count;
    }
    // #endregion
    processGroupHitTestInfo(newHitTestInfo, oldHitTestInfo) {
        const rowsArray = this.rowsArray;
        let isDirty = false;
        const newRowIdx = !isNullOrUndefined(newHitTestInfo) ? newHitTestInfo.rowIndex : -1;
        const oldRowIdx = !isNullOrUndefined(oldHitTestInfo) ? oldHitTestInfo.rowIndex : -1;
        const newRow = rowsArray[newRowIdx];
        if (newRowIdx === oldRowIdx && !isValidString(newRow === null || newRow === void 0 ? void 0 : newRow.infoIconTooltip)) {
            return;
        }
        if (newRow === null || newRow === void 0 ? void 0 : newRow.isGroupRow) {
            newRow.isDirty = true;
            isDirty = true;
        }
        const oldRow = rowsArray[oldRowIdx];
        if (oldRow === null || oldRow === void 0 ? void 0 : oldRow.isGroupRow) {
            oldRow.isDirty = true;
            isDirty = true;
        }
        if (isDirty) {
            this.needRedraw = true;
        }
    }
    processCellControlHitTestInfo(newHitTestInfo, oldHitTestInfo) {
        let isDirty = false;
        const newCell = this.getCellFromHitTestInfo(newHitTestInfo);
        const oldCell = this.getCellFromHitTestInfo(oldHitTestInfo);
        if (newCell === oldCell && oldCell !== null) {
            if (oldCell !== null) {
                if (oldCell.QuickTableEditingInfo === null) {
                    return;
                }
            }
            else {
                return;
            }
        }
        // Simulates cell mouseover.
        if (!isNullOrUndefined(newCell)) {
            const newQuickTableEditingInfo = newCell.QuickTableEditingInfo;
            if (!newCell.ReadOnly && newQuickTableEditingInfo) {
                this.needRedrawBackground = true;
                newCell.isDirty = true;
                isDirty = true;
            }
        }
        // Simulates cell mouseleave.
        if (!isNullOrUndefined(oldCell)) {
            const oldQuickTableEditingInfo = oldCell.QuickTableEditingInfo;
            if (!oldCell.ReadOnly && oldQuickTableEditingInfo) {
                this.needRedrawBackground = true;
                oldCell.isDirty = true;
                isDirty = true;
            }
        }
        if (isDirty) {
            this.needRedraw = true;
        }
    }
    getTableRowFromHitTestInfo(hitTestInfo) {
        if (isNullOrUndefined(hitTestInfo)) {
            return null;
        }
        const rowIdx = hitTestInfo.rowIndex;
        if (rowIdx === -1) {
            return null;
        }
        return this.rowsArray[rowIdx];
    }
    getCellFromHitTestInfo(hitTestInfo) {
        const row = this.getTableRowFromHitTestInfo(hitTestInfo);
        if (isNullOrUndefined(row)) {
            return null;
        }
        const colIdx = hitTestInfo.columnIndex;
        if (colIdx === -1) {
            return null;
        }
        return row.cells[this.sortedColumns[colIdx].PRIVATE.index];
    }
    BeginEdit(info, cell, row, columnIndex, realColumnIndex, event) {
        if (isNullOrUndefined(info) || !info.IsEditable) {
            return;
        }
        const args = {
            table: this,
            row,
            cell,
            columnIndex,
            realColumnIndex,
            controlType: info.ControlType,
            subItemIndex: null
        };
        switch (info.ControlType) {
            case DynProperty.ACCOUNT:
            case DynProperty.INSTRUMENT:
            case DynProperty.COMBOBOX_COMBOITEM_TIF:
            case DynProperty.COMBOBOX:
            case DynProperty.COLOR:
                this.OnExternalCellEditRequest.Raise(args);
                break;
            case DynProperty.DOUBLE:
                this.OnExternalCellEditRequest.Raise(args);
                event.preventDefault();
                break;
            case DynProperty.LINK:
            case DynProperty.LINK_WITH_TEXT:
            case DynProperty.BOOLEAN:
                this.AfterEditItem.Raise(args);
                break;
            case DynProperty.CELLBUTTON:
                this.AfterButtonItem.Raise(args);
                break;
            case DynProperty.EXPAND_SUPER_POSITION_BUTTON_AND_TEXT: {
                const rowRect = row.displayRectangle;
                const btnRect = this.getButtonRectangle(rowRect.X, rowRect.Y, rowRect.Width, rowRect.Height);
                btnRect.X =
                    rowRect.X + this.getXPadding(columnIndex, this.getColumnTextAlign(this.columns[columnIndex]));
                if (btnRect.Contains(this.lastMouseDown.X, this.lastMouseDown.Y)) {
                    this.AfterExpandItem.Raise(args, event);
                }
                break;
            }
            case DynProperty.INFO_PICTURE_RIGHT_AND_TEXT:
            case DynProperty.DELAYED_PICTURE_RIGHT_AND_TEXT: {
                const pr = this.sortedColumns[columnIndex].PRIVATE;
                const cellW = pr.drawingWidth;
                const cellX = pr.drawingX;
                const cellH = row.displayRectangle.Height;
                const cellY = row.displayRectangle.Y;
                let x = Math.round(cellX + cellW / 2 - 7) - 0.5;
                const cType = info.ControlType;
                if (cType === DynProperty.INFO_PICTURE_RIGHT_AND_TEXT ||
                    cType === DynProperty.DELAYED_PICTURE_RIGHT_AND_TEXT) {
                    x = Math.round(cellX + cellW - 7 - 17) - 0.5;
                }
                const y = Math.round(cellY + cellH / 2 - 7.5) - 0.5;
                const hover = QuickTable.isCellControlHovered(x, y, 17, 17, this.lastMouseDown.X, this.lastMouseDown.Y);
                if (hover) {
                    this.OnPaintedPictureButtonClick.Raise(args, event);
                }
                break;
            }
            case DynProperty.INFO_PICTURE:
            case DynProperty.ORDER_BUTTON:
            case DynProperty.TRADE_BUTTON:
            case DynProperty.CLOSE_BUTTON:
            case DynProperty.VISIBILITY_BUTTON:
            case DynProperty.SETTINGS_BUTTON:
            case DynProperty.SHOW_ON_CHART_BUTTON:
            case DynProperty.ITCHART_ADVANCED:
            case DynProperty.SELL_ASSET_BUTTON:
            case DynProperty.PLACE_BUTTON:
            case DynProperty.CLOSE_VIA_OE_BUTTON: {
                const pr = this.sortedColumns[columnIndex].PRIVATE;
                const cellW = pr.drawingWidth;
                const cellX = pr.drawingX;
                const cellH = row.displayRectangle.Height;
                const cellY = row.displayRectangle.Y;
                const btnRect = this.getButtonRectangle(cellX, cellY, cellW, cellH);
                if (btnRect.Contains(this.lastMouseDown.X, this.lastMouseDown.Y)) {
                    this.OnPaintedPictureButtonClick.Raise(args, event);
                }
                break;
            }
            case DynProperty.ALERT_BUTTONS_GROUP: {
                const paramColumn = this.sortedColumns[columnIndex].PRIVATE;
                const width = paramColumn.drawingWidth;
                const height = row.displayRectangle.Height;
                const x1 = Math.round(paramColumn.drawingX + width - 64) - 0.5;
                const x2 = x1 + 20;
                const x3 = x2 + 20;
                const y = Math.round(row.displayRectangle.Y + height / 2 - 7.5) - 0.5;
                const isHoverRectangle = QuickTable.isCellControlHovered(x1, y, width, height, this.lastMouseDown.X, this.lastMouseDown.Y);
                if (!isHoverRectangle) {
                    break;
                }
                let isHoveredImage = QuickTable.isCellControlHovered(x1, y, 17, 17, this.lastMouseDown.X, this.lastMouseDown.Y);
                if (isHoveredImage) {
                    args.subItemIndex = AlertSubImage.PLAY_STOP;
                    this.OnPaintedPictureButtonClick.Raise(args, event);
                    break;
                }
                isHoveredImage = QuickTable.isCellControlHovered(x2, y, 17, 17, this.lastMouseDown.X, this.lastMouseDown.Y);
                if (isHoveredImage) {
                    args.subItemIndex = AlertSubImage.UPDATE;
                    this.OnPaintedPictureButtonClick.Raise(args, event);
                    break;
                }
                isHoveredImage = QuickTable.isCellControlHovered(x3, y, 17, 17, this.lastMouseDown.X, this.lastMouseDown.Y);
                if (isHoveredImage) {
                    args.subItemIndex = AlertSubImage.CLOSE;
                    this.OnPaintedPictureButtonClick.Raise(args, event);
                    break;
                }
                break;
            }
        }
    }
    afterExternalCellEdit(value, tag) {
        const controlType = tag.cell.QuickTableEditingInfo.ControlType;
        switch (controlType) {
            case DynProperty.DOUBLE:
                tag.newValue = Number(MathUtils.formatValueWithEps(value));
                break;
            case DynProperty.COMBOBOX:
            case DynProperty.COMBOBOX_COMBOITEM_TIF:
            case DynProperty.ACCOUNT:
            case DynProperty.INSTRUMENT:
                tag.newValue = value;
                break;
            case DynProperty.COLOR:
                tag.newValue = Color.rgbaToHex(value);
                break;
        }
        this.AfterEditItem.Raise(tag);
        this.redraw();
    }
    CorrectColumnsWidth(change) {
        let visColCount = 0;
        const startIndex = this.sortedColumns.indexOf(this.sortedColumns[this.activeColumnIndex]);
        for (let i = startIndex + 1; i < this.sortedColumns.length; i++) {
            if (this.sortedColumns[i].visible && !this.sortedColumns[i].hidden) {
                visColCount++;
            }
        }
        let remainder = 0;
        if (visColCount > 0) {
            const colChange = change / visColCount;
            for (let i = startIndex + 1; i < this.sortedColumns.length; i++) {
                const sortCol = this.sortedColumns[i];
                if (sortCol.visible && !sortCol.hidden) {
                    let newWidth = sortCol.width - colChange;
                    if (newWidth <= 7) {
                        remainder += 7 - newWidth;
                        newWidth = 7;
                    }
                    sortCol.width = newWidth;
                }
            }
        }
        return remainder;
    }
    onMouseUp(event) {
        const hitTestInfo = this.HitTest(event.offsetX, event.offsetY);
        // Click by scroll
        if (hitTestInfo.zone === QuickTableZone.VerticalScroll) {
            this.scroll.mouseUp(event);
        }
        if (event.button === MouseButtons.Left &&
            !this.columnReordering /* && !ReadOnly */ &&
            hitTestInfo.zone === QuickTableZone.Header &&
            this.movingType === QuickTableMovingType.None &&
            this.activeColumnIndex !== -1) {
            if (hitTestInfo.columnIndex !== -1 && !this.lockManualSorting) {
                const htColumnIndex = hitTestInfo.columnIndex;
                const htColumn = this.sortedColumns[htColumnIndex];
                if (!isNullOrUndefined(htColumn)) {
                    const realColumnIndex = htColumn.PRIVATE.index;
                    if (this.sortIndex == realColumnIndex) {
                        this.asc = !this.asc;
                    }
                    else {
                        this.sortIndex = realColumnIndex;
                    }
                    this.sortRowsArray();
                }
            }
        }
        else if (this.movingType === QuickTableMovingType.ColumnMoving &&
            this.activeColumnIndex !== -1 &&
            this.lastMouseMoveHitTestInfo != null &&
            this.lastMouseMoveHitTestInfo.columnIndex !== -1) {
            const lastInd = this.lastMouseMoveHitTestInfo.columnIndex;
            const activeColumnIndex = this.activeColumnIndex;
            if (lastInd !== this.activeColumnIndex) {
                let prev = false;
                if (this.sortedColumns[lastInd].displayedIndex -
                    this.sortedColumns[activeColumnIndex].displayedIndex ===
                    1) {
                    /* empty */
                }
                else if (this.sortedColumns[lastInd].displayedIndex -
                    this.sortedColumns[activeColumnIndex].displayedIndex ===
                    -1) {
                    prev = true;
                }
                else if (event.offsetX - this.sortedColumns[lastInd].PRIVATE.drawingX <
                    this.sortedColumns[lastInd].PRIVATE.drawingWidth / 2) {
                    prev = true;
                }
                this.SwitchDisplayIndex(this.sortedColumns[activeColumnIndex], this.sortedColumns[lastInd], prev);
            }
        }
        this.activeColumnIndex = -1;
        this.movingType = QuickTableMovingType.None;
        this.redraw();
        // Document.releaseCapture() is deprecated. Use Element.releasePointerCapture() instead. More information https://developer.mozilla.org/docs/Web/API/Element/releasePointerCapture
        if (document.releaseCapture) {
            document.releaseCapture();
        }
        this.updateCursor(hitTestInfo);
        this.mouseDownWasOnMe = false;
        this.onTableMouseUP.Raise(hitTestInfo);
    }
    onDoubleClick(event) {
        const hittestInfo = this.HitTest(event.original.offsetX, event.original.offsetY);
        if (hittestInfo.zone === QuickTableZone.Header) {
            return false;
        }
        this.onTableMouseDoubleClick.Raise(this, hittestInfo);
        return true;
    }
    onMouseWheel(event) {
        if (!this.scroll.isActive()) {
            return;
        }
        this.scroll.mouseWheel(ScrollUtils.IsScrollUp(event.deltaY));
    }
    onKeyUp() { }
    onKeyDown() {
        if (this.trySelectAll())
            return;
        const keyProc = KeyEventProcessor;
        this.trySelectUpDown(keyProc.currentButton, keyProc.isShiftModOnly());
    }
    trySelectAll() {
        const keyProc = KeyEventProcessor;
        if (!keyProc.isCtrlModOnly() || keyProc.currentButton !== KeyCode.A) {
            return;
        }
        const selRowIds = this.selectedRowIds;
        selRowIds.splice(0, selRowIds.length);
        const rowsArray = this.rowsArray;
        const len = rowsArray.length;
        for (let i = 0; i < len; i++) {
            const row = rowsArray[i];
            if (row.isGroupRow)
                continue;
            selRowIds.push(row.id);
        }
        this.afterSelectionChanged(true);
        return true;
    }
    selectById(idArray) {
        if ((idArray === null || idArray === void 0 ? void 0 : idArray.length) === 0) {
            return;
        }
        const newSelRowIds = [];
        const rowDict = this.rows;
        const len = idArray.length;
        for (let i = 0; i < len; i++) {
            const id = idArray[i];
            const row = rowDict[id];
            if (!(row === null || row === void 0 ? void 0 : row.isGroupRow)) {
                newSelRowIds.push(id);
            }
        }
        if (newSelRowIds.length === 0) {
            return;
        }
        this.selectedRowIds = newSelRowIds;
        this.afterSelectionChanged(true);
    }
    trySelectUpDown(key, multiSelect) {
        if (key !== KeyCode.UP &&
            key !== KeyCode.DOWN &&
            key !== KeyCode.HOME &&
            key !== KeyCode.END &&
            key !== KeyCode.PAGE_UP &&
            key !== KeyCode.PAGE_DOWN) {
            return;
        }
        const rowsArray = this.rowsArray;
        const rowsArrayLen = rowsArray.length;
        if (rowsArrayLen === 0)
            return;
        const rows = this.rows;
        let selRowIds = this.selectedRowIds;
        const up = key === KeyCode.UP || key === KeyCode.HOME || key === KeyCode.PAGE_UP;
        if (selRowIds.length === 0 && !up && !rowsArray[0].isGroupRow) {
            selRowIds.push(rowsArray[0].id);
            this.afterSelectionChanged();
            return true;
        }
        else if (selRowIds.length > 0) {
            // важно направление выделения
            const startIdx = rowsArray.indexOf(rows[selRowIds[0]]);
            let lastIdx = rowsArray.indexOf(rows[selRowIds[selRowIds.length - 1]]);
            // если область выделения уменьшается
            if (multiSelect &&
                ((up && startIdx < lastIdx) || (!up && startIdx > lastIdx)) &&
                selRowIds.indexOf(rowsArray[lastIdx].id)) {
                const idx = selRowIds.indexOf(rowsArray[lastIdx].id);
                if (idx !== -1) {
                    selRowIds.splice(idx, 1);
                    this.afterSelectionChanged();
                    return true;
                }
            }
            else {
                // если область выделения увеличивается, иначе выбирается новая строка
                lastIdx += up ? -1 : 1;
                // ищем видимую строку, пока не достигаем края массива
                while ((up && lastIdx >= 0) || (!up && lastIdx < rowsArray.length)) {
                    const lastIdxRow = rowsArray[lastIdx];
                    if ((lastIdxRow === null || lastIdxRow === void 0 ? void 0 : lastIdxRow.isVisible()) &&
                        !lastIdxRow.isGroupRow /* && lastIdxRow.selectable &&
                lastIdxRow.visibleByGrouping && lastIdxRow.visibleBySearching */) {
                        if (!multiSelect) {
                            selRowIds = [];
                        } // выделяется только одна строка
                        selRowIds.push(lastIdxRow.id);
                        this.selectedRowIds = selRowIds;
                        this.afterSelectionChanged();
                        return true;
                    }
                    lastIdx += up ? -1 : 1; // определяется направление выделения, -1: вверх, 1: вниз
                }
            }
        }
    }
    afterSelectionChanged(disallowScrollToLastSelected = false) {
        const scroll = this.scroll;
        const selRowIds = this.selectedRowIds;
        if (!disallowScrollToLastSelected && scroll.Visible && selRowIds.length) {
            const lastSelectedRow = this.rows[selRowIds[selRowIds.length - 1]];
            const idx = this.rowsArray.indexOf(lastSelectedRow);
            if (idx !== -1) {
                scroll.trailingScroll(idx);
            }
        }
        // Erasing hover as keyboard navigation is being used.
        this.hoveredRow = null;
        this.OnSelectionChanged.Raise(false, this);
        if (!this.isUpdatingNow) {
            this.redraw();
        }
    }
    // TODO. Refactor.
    // Get info about clicked area
    HitTest(x, y) {
        const res = new HitTestInfo(x, y);
        const sortedColumns = this.sortedColumns;
        const lastVisibleSortedColumnIdx = this.lastVisibleSortedColumnIdx;
        // Click on scroll
        if (this.scroll.isActive() && this.scroll.containerRectangle.Contains(x, y)) {
            res.zone = QuickTableZone.VerticalScroll;
        }
        else if (this.showHeader && y > this.borderWidth && y < this.rowHeight) {
            // Click on header
            res.zone = QuickTableZone.Header;
            let curX = 0;
            for (let i = 0; i < sortedColumns.length; i++) {
                const sortedColumn = sortedColumns[i];
                // skip unvisible column
                if (!sortedColumn.visible || sortedColumn.hidden) {
                    continue;
                }
                if (lastVisibleSortedColumnIdx === i) {
                    res.lastVisibleSortedColumn = true;
                }
                const drawingWidth = sortedColumn.PRIVATE.drawingWidth;
                const rightBorder = curX + drawingWidth;
                // Click on header grid?
                if (x > rightBorder - QuickTable.ResizeColumnsAreaPixel &&
                    x < rightBorder + QuickTable.ResizeColumnsAreaPixel &&
                    !this.isActiveEditControl) {
                    res.zone = QuickTableZone.HeaderGrid;
                    res.columnIndex = i;
                    break;
                }
                if (x > curX && x < rightBorder) {
                    res.columnIndex = i;
                    res.realColumnIndex = this.columns.indexOf(sortedColumn);
                    break;
                }
                curX += drawingWidth;
            }
        }
        else {
            /* Click on rows area */
            const ClientH = this.height;
            const rowsArray = this.rowsArray;
            const len = !isNullOrUndefined(rowsArray) ? rowsArray.length : 0;
            for (let i = this.getFirstVisibleScrollIndex(); i < len; i++) {
                const row = rowsArray[i];
                if (!row.isVisible())
                    continue;
                const rowRect = row.displayRectangle;
                if (rowRect.Contains(x, y)) {
                    res.rowIndex = i;
                    break;
                }
                // search only in displayed rows
                if (rowRect.Y > ClientH) {
                    break;
                }
            }
            if (res.rowIndex !== -1) {
                const useVerticalSeparatorForResizing = this.useVerticalSeparatorForResizing;
                const ResizeColumnsAreaPixel = QuickTable.ResizeColumnsAreaPixel;
                let curX = 0;
                for (let i = 0; i < sortedColumns.length; i++) {
                    const sortedColumn = sortedColumns[i];
                    // skip unvisible column
                    if (!sortedColumn.visible || sortedColumn.hidden) {
                        continue;
                    }
                    if (lastVisibleSortedColumnIdx === i) {
                        res.lastVisibleSortedColumn = true;
                    }
                    const drawingWidth = sortedColumn.PRIVATE.drawingWidth;
                    const rightBorder = curX + drawingWidth;
                    // Click on header grid?
                    if (useVerticalSeparatorForResizing &&
                        x > rightBorder - ResizeColumnsAreaPixel &&
                        x < rightBorder + ResizeColumnsAreaPixel) {
                        res.zone = QuickTableZone.VerticalResizeSeparator;
                        res.columnIndex = i;
                        res.realColumnIndex = this.columns.indexOf(sortedColumn);
                        break;
                    }
                    if (x > curX && x < rightBorder) {
                        res.columnIndex = i;
                        res.realColumnIndex = this.columns.indexOf(sortedColumn);
                        break;
                    }
                    curX += drawingWidth;
                }
            }
        }
        return res;
    }
    setHiddenColumnsData(hiddenColumnsData) {
        var _a;
        if (isNullOrUndefined(hiddenColumnsData) || ((_a = this.columns) === null || _a === void 0 ? void 0 : _a.length) === 0) {
            return;
        }
        let columnsChanged = false;
        const columns = this.columns;
        for (const colIdx in hiddenColumnsData) {
            const hide = hiddenColumnsData[colIdx];
            const column = columns[colIdx];
            if (!isNullOrUndefined(column)) {
                column.hidden = hide;
                columnsChanged = true;
            }
        }
        if (columnsChanged) {
            this.UpdateSortedColumns();
            this.redraw();
            this.populateHeaderContextMenu(columns);
            this.OnVisibleColumnChanged.Raise(this.getVisibleColumn(), this);
        }
    }
    switchColumnVisibility(column) {
        column.visible = !column.visible;
        this.UpdateSortedColumns();
        this.redraw();
        this.OnVisibleColumnChanged.Raise(this.getVisibleColumn(), this);
    }
    getVisibleColumn() {
        const len = this.columns.length;
        const result = [];
        for (let i = 0; i < len; i++) {
            const col = this.columns[i];
            if (col.visible && !col.hidden) {
                result.push(i);
            }
        }
        return result;
    }
    setVisibleColumn(visibleArr) {
        let len = this.columns.length;
        for (let i = 0; i < len; i++) {
            this.columns[i].visible = false;
        }
        len = visibleArr.length;
        for (let i = 0; i < len; i++) {
            this.columns[visibleArr[i]].visible = true;
        }
        this.UpdateSortedColumns();
        this.redraw();
        this.OnVisibleColumnChanged.Raise(visibleArr, this);
    }
    createGroupByContextMenuItem(columns) {
        const groupByColumnIndex = this.groupByColumnIndex;
        const len = columns.length;
        const items = [];
        for (let i = 0; i < len; i++) {
            const column = columns[i];
            if (column.hidden || !column.allowGrouping) {
                continue;
            }
            items.push({
                type: NewContextMenuItemType.Default,
                text: column.headerKey,
                selected: column.PRIVATE.index === groupByColumnIndex,
                tag: column,
                event: (item) => {
                    this.onGroupByMenuItemClicked(item);
                }
            });
        }
        if (items.length === 0) {
            this.groupByContextMenuItem = null;
            return;
        }
        items.sort(QuickTable.QuickTableMenuComparer);
        this.groupByContextMenuItem = {
            type: NewContextMenuItemType.Default,
            text: 'panel.menu.GroupBy',
            subitems: items
        };
    }
    onGroupByMenuItemClicked(menuItem) {
        if (isNullOrUndefined(this.groupByContextMenuItem)) {
            return;
        }
        let index = -1;
        for (let i = 0; i < this.groupByContextMenuItem.subitems.length; i++) {
            const groupByMenuItem = this.groupByContextMenuItem.subitems[i];
            if (groupByMenuItem === menuItem) {
                groupByMenuItem.selected = !groupByMenuItem.selected;
                if (groupByMenuItem.selected) {
                    index = groupByMenuItem.tag.PRIVATE.index;
                }
                index = groupByMenuItem.selected ? groupByMenuItem.tag.PRIVATE.index : -1;
            }
            else {
                groupByMenuItem.selected = false;
            }
        }
        this.setGroupByColumnIdx(index);
    }
    populateHeaderContextMenu(columns) {
        var _a;
        let len = columns.length;
        const items = [];
        for (let i = 0; i < len; i++) {
            const column = columns[i];
            if (column.hidden || !isValidString(column.headerText)) {
                continue;
            }
            items.push({
                type: NewContextMenuItemType.Default,
                text: column.headerKey,
                canCheck: true,
                closeOnClick: false,
                checked: column.visible,
                tag: column,
                event: (item) => {
                    this.switchColumnVisibility(item.tag);
                }
            });
        }
        if ((_a = this.AssociatedItem) === null || _a === void 0 ? void 0 : _a.NeedSortMenu()) {
            items.sort(QuickTable.QuickTableMenuComparer);
        }
        len = items.length;
        for (let i = 0; i < len; i++) {
            const column = items[i].tag;
            if (column.beginGroup) {
                items.splice(i, 0, { type: NewContextMenuItemType.Separator });
                i++;
            }
        }
        if (items.length > 0) {
            items.push({ type: NewContextMenuItemType.Separator });
            items.push({
                type: NewContextMenuItemType.Default,
                text: 'panel.menu.resetToDefault',
                canCheck: false,
                enabled: true,
                event: this.resetContextMenuToDefault.bind(this)
            });
        }
        this.headerContextMenuItems = items;
    }
    resetContextMenuToDefault() {
        const contextMenuItems = this.headerContextMenuItems;
        for (let i = 0; i < contextMenuItems.length; i++) {
            const contextMenuItem = contextMenuItems[i];
            if (contextMenuItem.canCheck) {
                contextMenuItem.tag.visible = contextMenuItem.tag.visibleDefault;
                contextMenuItem.checked = contextMenuItem.tag.visible;
            }
        }
        this.redraw();
        hideContextMenu();
        this.OnVisibleColumnChanged.Raise(this.getVisibleColumn(), this);
    }
    static QuickTableMenuComparer(x, y) {
        if (x && y) {
            return x.tag.MenuIndex - y.tag.MenuIndex;
        }
        else {
            return 0;
        }
    }
    setTableContextMenuItems(newItems) {
        if (isValidArray(newItems)) {
            this.tableContextMenuItems = newItems;
        }
        else {
            this.tableContextMenuItems = [];
        }
    }
    createTableContextMenuItems() {
        let menuItems = [];
        if (isValidArray(this.tableContextMenuItems)) {
            menuItems = menuItems.concat(this.tableContextMenuItems);
        }
        if (this.allowGroupBy && !isNullOrUndefined(this.groupByContextMenuItem)) {
            menuItems = menuItems.concat(this.groupByContextMenuItem);
        }
        return menuItems;
    }
    onResize() {
        // Scroll resizing.
        this.scroll.setBounds(this.getScrollRect());
        this.prevWidth = this.width;
        this.redraw();
    }
    getScrollRect() {
        const totalHeinght = this.ShowTotals ? 25 : 0;
        // var left = this.width - Scroll.SCROLL_WIDTH - 1;
        const left = this.width - Scroll.SCROLL_WIDTH;
        // var top = this.showHeader ? this.rowHeight + 1 : this.borderWidth;
        const top = this.showHeader ? this.rowHeight : 0;
        // var height = this.height - 2 * this.borderWidth - top + 1;
        const height = this.height - /* 2 * this.borderWidth - */ top - totalHeinght;
        const width = Scroll.SCROLL_WIDTH;
        return new Rectangle(left, top, width, height);
    }
    SetScrollHidden(value) {
        if (!isNullOrUndefined(this.scroll)) {
            this.scroll.Hidden = value;
        }
    }
    // TODO. Use it for refactoring!
    getRowsArrayToDraw() {
        const resultRowsArray = [];
        const scrollIndex = this.scroll.scrollIndex;
        let visibleRowCounter = 0;
        let curY = this.showHeader ? this.rowHeight : this.borderWidth;
        const clientH = Math.floor(this.height);
        const rowsArray = this.rowsArray;
        const len = rowsArray.length;
        for (let i = 0; i < len; i++) {
            if (curY >= clientH) {
                break;
            }
            const row = rowsArray[i];
            if (!row.isVisible()) {
                continue;
            }
            visibleRowCounter++;
            if (visibleRowCounter - scrollIndex < 0) {
                continue;
            }
            resultRowsArray.push(row);
            curY += this.rowHeight;
        }
        return resultRowsArray;
    }
    getRowsCountByHeight() {
        return Math.floor(this.height / this.rowHeight);
    }
    // TODO. Optimize.
    // Use this method instead of scroll.scrollIndex.
    getFirstVisibleScrollIndex() {
        const visibleFirstScrollIndex = this.scroll.scrollIndex;
        const rowsArray = this.rowsArray;
        const len = rowsArray.length;
        let visibleRowsCount = 0;
        for (let i = 0; i < len; i++) {
            if (visibleRowsCount === visibleFirstScrollIndex) {
                return i;
            }
            const row = rowsArray[i];
            if (row.isVisible()) {
                visibleRowsCount++;
            }
        }
        return 0;
    }
    updateScrollElementsCount(forceUpdate = false) {
        let count = 0;
        const rowsArray = this.rowsArray;
        const len = rowsArray.length;
        for (let i = 0; i < len; i++) {
            if (rowsArray[i].isVisible()) {
                count++;
            }
        }
        const scroll = this.scroll;
        if (!isNullOrUndefined(scroll)) {
            scroll.elementHeight = this.rowHeight;
            scroll.setScrollElementsCount(count, forceUpdate);
        }
        this.onScrollElementsCountChanged();
    }
    SwitchDisplayIndex(column1, column2, prev) {
        if (column1 == null || column2 == null || column1 === column2) {
            return;
        }
        let tempArray = [];
        tempArray = tempArray.concat(this.sortedColumns);
        ArrayUtils.RemoveElementFromArray(tempArray, column1);
        tempArray.splice(tempArray.indexOf(column2) + (prev ? 0 : 1), 0, column1);
        for (let i = 0; i < tempArray.length; i++) {
            tempArray[i].displayedIndex = i;
        }
        this.UpdateSortedColumns();
        // this.needRedraw = true;
        this.needRedrawBackground = true;
        // call event
        if (this.ColumnDisplayIndexChanged != null) {
            this.ColumnDisplayIndexChanged.Raise(this, this.sortedColumns.indexOf(column1));
        }
    }
    themeChange() {
        const colors = ThemeManager.colors;
        this.BackColor = this.isAttached ? colors.attachedQuickTableBackColor : colors.detachedQuickTableBackColor;
        this.headerBackColor = this.isAttached
            ? colors.attachedQuickTableHeaderBackColor
            : colors.detachedQuickTableHeaderBackColor;
        this.headerBackBrush = new SolidBrush(this.headerBackColor);
        this.headerForeColor = colors.quickTableHeaderForeColor;
        this.headerTextColorBrush = new SolidBrush(this.headerForeColor);
        this.ForeColor = colors.quickTableForeColor;
        this.selectedBackColor = colors.selectedRowBackColor;
        this.selectedHoveredBackColor = colors.selectedRowBackColor;
        this.selectedForeColor = colors.TableSelectionForeColor;
        this.hoveredHeaderBackColor = this.hoveredBackColor = colors.hoveredRowBackColor;
        this.rowBackColor = this.isAttached ? colors.attachedQuickTableBackColor : colors.detachedQuickTableBackColor;
        this.secondRowBackColor = this.isAttached
            ? colors.attachedQuickTableSecondBackColor
            : colors.detachedQuickTableSecondBackColor;
        this.totalsBackColor = this.isAttached
            ? colors.attachedQuickTableBackColor
            : colors.detachedQuickTableBackColor;
        this.totalsForeColor = colors.tableTotalsForeground;
        this.totalsLineSeparatorColor = colors.tableTotalsLineSeparator;
        this.hoveredBorderColor = colors.quickTableHoveredBorderColor;
        this.columnSliderColor = colors.tableHighlightCellBorderInSelectedRow;
        this.dragDefaultPen = new Pen(colors.quickTableCellDragDefaultColor, 1, PenStyle.DragLine);
        this.dragHighlightPen = new Pen(this.columnSliderColor, 1, PenStyle.DragLine);
        this.editHighlightPenInHoveredRow = new Pen(colors.tableHighlightCellBorderInHoveredRow, 1, PenStyle.EditLine);
        this.editHighlightPenInSelectedRow = new Pen(colors.tableHighlightCellBorderInSelectedRow, 1, PenStyle.EditLine);
        this.tableGroupOpenImg = ThemeManager.NewImages.tableGroupOpenDefault.image;
        this.tableGroupCloseImg = ThemeManager.NewImages.tableGroupCloseDefault.image;
        const groupRowBackColor = this.isAttached
            ? colors.attachedQuickTableGroupRowBackColor
            : colors.detachedQuickTableGroupRowBackColor;
        this.tableGroupRowDefaultBrush = new SolidBrush(groupRowBackColor);
        this.tableGroupRowHoveredBrush = new SolidBrush(colors.tableGroupRowHoveredBackground);
        this.scroll.themeChange();
        this.populateHeaderContextMenu(this.columns);
        this.redraw();
    }
    // TODO. Call only from QuickTableRactive.
    localize() {
        let len = this.columns.length;
        for (let i = 0; i < len; i++) {
            this.columns[i].headerText = Resources.getResource(this.columns[i].headerKey);
        }
        this.LinkText = Resources.getResource('InstrumentDetailsPanel.YTM.RefreshLink'); // For DrawLinkWithText
        // TODO. Remove after implementing groups.
        const rows = this.rowsArray;
        len = rows ? rows.length : 0;
        for (let i = 0; i < len; i++) {
            const row = rows[i];
            if (row.isGroupRow) {
                row.localize();
            }
        }
        this.populateHeaderContextMenu(this.columns);
        this.createGroupByContextMenuItem(this.columns);
        this.needRedraw = true;
    }
    getColumnByHeaderKey(headerKey) {
        const columns = this.columns;
        if (isNullOrUndefined(columns))
            return null;
        for (let i = 0, len = columns.length; i < len; i++) {
            const col = columns[i];
            if (col.headerKey === headerKey) {
                return col;
            }
        }
        return null;
    }
    getCellRectangleRelativeToCanvas(row, columnIndex) {
        const sortedColumns = this.sortedColumns;
        const len = sortedColumns.length;
        let columnLeft = 0;
        let cellW = 0;
        for (let i = 0; i < len; i++) {
            const cc = sortedColumns[i];
            if (!cc.visible) {
                continue;
            }
            cellW = cc.PRIVATE.drawingWidth;
            if (i === columnIndex) {
                break;
            }
            columnLeft += cellW;
        }
        if (columnIndex === len - 1) {
            /* #130315 */
            const scrollWidth = this.scroll.Visible && !this.scroll.HiddenByScrollAutohide ? Scroll.SCROLL_WIDTH : 0;
            cellW = cellW - scrollWidth;
            if (cellW < 0)
                cellW = 0;
        }
        const rowRect = row.displayRectangle;
        // TODO.
        let y = rowRect.Y;
        if (rowRect.IsEmpty()) {
            const rowIdx = this.rowsArray.indexOf(row);
            if (!this.AddToEnd && rowIdx === 0) {
                y = this.showHeader ? this.rowHeight : this.borderWidth;
            }
        }
        return new Rectangle(columnLeft, y, cellW, this.rowHeight);
    }
    // TODO. Refactor. isIntColumn.
    GetGroupKey(row) {
        const groupByColumnIndex = this.groupByColumnIndex;
        if (groupByColumnIndex === -1) {
            return null;
        }
        return row.cells[this.groupByColumnIndex].groupByValue;
    }
    redraw() {
        this.needRedrawBackground = true;
        this.needRedraw = true;
    }
    getTableHeight(rowsCount) {
        let height = this.borderWidth;
        if (this.showHeader) {
            height += this.rowHeight;
        }
        height += rowsCount * this.rowHeight;
        height += this.borderWidth;
        return height;
    }
    // #region ISerializable
    serialize() {
        const colsArr = [];
        const columns = this.columns;
        for (let i = 0; i < this.columns.length; i++) {
            const col = columns[i];
            colsArr.push({
                key: col.headerKey,
                width: col.width,
                visible: col.visible ? 1 : 0,
                displayIndex: col.displayedIndex
            });
        }
        return {
            sortIndex: this.sortIndex,
            sortOrder: this.asc,
            columns: colsArr
        };
    }
    deserialize(serialized) {
        this.sortIndex = serialized.sortIndex;
        this.asc = serialized.sortOrder;
        const columns = serialized.columns;
        for (let i = 0; i < columns.length; i++) {
            const serializedColumn = columns[i];
            const column = this.getColumnByHeaderKey(serializedColumn.key);
            if (isNullOrUndefined(column)) {
                continue;
            }
            if (isValidNumber(serializedColumn.width)) {
                column.width = serializedColumn.width;
            }
            if (!isNullOrUndefined(serializedColumn.visible)) {
                column.visible = serializedColumn.visible;
            }
            if (isValidNumber(serializedColumn.displayIndex)) {
                column.displayedIndex = serializedColumn.displayIndex;
            }
        }
        if (this.sortIndex >= this.columns.length) {
            this.sortIndex = -1;
        }
        this.UpdateSortedColumns();
        this.populateHeaderContextMenu(this.columns);
    }
    deserializeOldScheme(serialized) {
        if (isNullOrUndefined(serialized))
            return;
        const table = serialized.table;
        if (isNullOrUndefined(table))
            return;
        if (table.hasOwnProperty('sortIndex')) {
            this.sortIndex = parseInt(table.sortIndex);
        }
        if (table.hasOwnProperty('sortOrder')) {
            this.asc = !!parseInt(table.sortOrder);
        }
        const colsArr = table.columns;
        if (!isNullOrUndefined(colsArr)) {
            for (let i = 0, len = colsArr.length; i < len; i++) {
                const colData = colsArr[i];
                const col = this.getColumnByHeaderKey(colData.name);
                if (isNullOrUndefined(col))
                    continue;
                if (colData.hasOwnProperty('headerText')) {
                    col.headerText = colData.headerText;
                }
                if (colData.hasOwnProperty('width')) {
                    col.width = parseFloat(colData.width);
                }
                if (colData.hasOwnProperty('visible')) {
                    col.visible = !!parseInt(colData.visible);
                }
                if (colData.hasOwnProperty('displayIndex')) {
                    col.displayedIndex = parseInt(colData.displayIndex);
                }
            }
        }
        if (this.sortIndex >= this.columns.length) {
            this.sortIndex = -1;
        }
        this.UpdateSortedColumns();
        this.populateHeaderContextMenu(this.columns);
    }
    // #endregion
    updateLastWKoef(tableWidth) {
        let wKoef = 1;
        let totalW = 0;
        for (let i = 0; i < this.columns.length; i++) {
            if (!this.columns[i].visible || this.columns[i].hidden) {
                continue;
            }
            totalW += this.columns[i].width;
        }
        wKoef = totalW > 0 ? tableWidth / totalW : 0;
        this.lastWKoef = wKoef;
    }
    drawTableHeader(context, curX, hitTestInfo) {
        var _a;
        context.beginPath();
        context.font = this.headerFont.toString();
        const sortedColumns = this.sortedColumns;
        const hoveredHeaderCol = (hitTestInfo === null || hitTestInfo === void 0 ? void 0 : hitTestInfo.zone) === QuickTableZone.Header &&
            this.movingType !== QuickTableMovingType.ColumnResize &&
            this.movingType !== QuickTableMovingType.ColumnMoving
            ? hitTestInfo.columnIndex
            : -1;
        for (let i = 0; i < this.columns.length; i++) {
            const curColumn = sortedColumns[i];
            if (!curColumn.visible || curColumn.hidden) {
                continue;
            }
            let drawingWidth = Math.ceil(curColumn.width * this.lastWKoef);
            if (drawingWidth + curX >= this.width) {
                drawingWidth = this.width - this.borderWidth - curX;
            }
            curColumn.PRIVATE.drawingWidth = drawingWidth;
            curColumn.PRIVATE.drawingX = curX;
            if (this.showHeader) {
                let cellBackColor = this.headerBackColor;
                if (hoveredHeaderCol === i) {
                    cellBackColor = this.hoveredHeaderBackColor;
                }
                const cellBackBrush = new SolidBrush(cellBackColor);
                context.textAlign = this.getColumnTextAlign(curColumn);
                const headerRect = new Rectangle(curX + this.borderWidth, this.borderWidth, drawingWidth + 2 * this.borderWidth, this.rowHeight - 2 * this.borderWidth);
                context.FillRectWithRect(cellBackBrush, headerRect);
                let sortRect = Rectangle.Empty();
                const isSortCol = curColumn.PRIVATE.index === this.sortIndex;
                if (isSortCol) {
                    const sortImgSize = this.rowHeight;
                    const x = curX + drawingWidth - this.borderWidth - sortImgSize;
                    const y = (this.rowHeight + this.borderWidth - sortImgSize) / 2;
                    sortRect = new Rectangle(x, y, sortImgSize, sortImgSize);
                }
                // /* Draw header text */
                const textX = curX + this.borderWidth;
                const xPadding = this.getXPadding(curColumn.PRIVATE.index, context.textAlign);
                const textDrawingWidth = drawingWidth - this.borderWidth - sortRect.Width + (isSortCol ? xPadding : 0);
                const headerY = this.getYPadding(this.rowHeight, QuickTableUtils.GetTextHeight(context));
                const textBrush = new SolidBrush((_a = this.ColorHeaderText) !== null && _a !== void 0 ? _a : this.headerForeColor);
                QuickTableUtils.DrawTextInRect(context, curColumn.headerText, this.headerFont, textBrush, textX, this.borderWidth, textDrawingWidth, this.rowHeight - 2 * this.borderWidth, xPadding, headerY);
                if (!sortRect.IsEmpty()) {
                    const sortImg = this.asc
                        ? ThemeManager.NewImages.sortUpDefault.image
                        : ThemeManager.NewImages.sortDownDefault.image;
                    if (!isNullOrUndefined(sortImg)) {
                        context.FillRectWithRect(cellBackBrush, sortRect);
                        context.DrawCenteredImage(sortImg, sortRect, sortRect.Width - 8 * 2, sortRect.Height - 8 * 2);
                    }
                }
            }
            curX += drawingWidth;
        }
        context.stroke();
    }
    drawTotalRow(context, rowK0, columnsTotalValues) {
        if (!this.ShowTotals) {
            return;
        }
        context.save();
        const cellH = this.rowHeight;
        const cellY = this.height - cellH;
        context.fillStyle = this.totalsBackColor;
        context.fillRect(1, cellY, this.width, cellH);
        let curX = 0;
        for (let j = 0; j < this.sortedColumns.length; j++) {
            const curColumn = this.sortedColumns[j];
            if (!curColumn.visible || curColumn.hidden) {
                continue;
            }
            const totalValue = columnsTotalValues.shift();
            if (!curColumn.isShowTotal()) {
                curX += curColumn.PRIVATE.drawingWidth;
                continue;
            }
            const cellW = curColumn.PRIVATE.drawingWidth;
            const cellX = curX;
            context.textAlign = this.getColumnTextAlign(curColumn);
            context.textBaseline = 'middle';
            let val = '';
            if (!isNullOrUndefined(rowK0 === null || rowK0 === void 0 ? void 0 : rowK0.item)) {
                val = rowK0.item.getFormatTotalValue(curColumn.PRIVATE.index, totalValue, this.totalCurrency);
            }
            const xPadding = this.getXPadding(curColumn.PRIVATE.index, context.textAlign);
            context.save();
            context.beginPath();
            context.rect(cellX, cellY, cellW, cellH);
            context.clip();
            const rectBrush = new SolidBrush(this.totalsBackColor);
            context.FillRect(rectBrush, cellX, cellY, cellW, cellH);
            const textBrush = new SolidBrush(this.totalsForeColor);
            QuickTableUtils.DrawTextInRect(context, val, this.tableFont, textBrush, cellX, cellY, cellW, cellH, xPadding, null, true);
            context.restore();
            curX += cellW;
        }
        const linePen = new Pen(this.totalsLineSeparatorColor, 1);
        context.DrawLine(linePen, 0, cellY, this.width, cellY);
        context.restore();
    }
    defaultReadOnlyCell(context, row, rowHovered, rowSelected, curColumn, cell, cellBackColor, cellRectangle, lastMouseMoveX, lastMouseMoveY) {
        var _a;
        let foreColor = this.getCellForegroundColor(row, cell, rowSelected, rowHovered, curColumn.PRIVATE.index);
        const eventArgs = new QuickTableDrawingCellEventArgs(this, row, curColumn.PRIVATE.index, cell, context.font, foreColor, cellBackColor, cellRectangle.X, cellRectangle.Y, cellRectangle.Width, cellRectangle.Height, rowHovered, lastMouseMoveX, lastMouseMoveY);
        if (!isNullOrUndefined(curColumn.renderer)) {
            curColumn.renderer.DrawCell(context, eventArgs);
        }
        else {
            this.OnDrawCell.Raise(context, eventArgs);
        }
        const isHandled = eventArgs.handled;
        if (!isHandled) {
            if (isValidString(cell.textPattern)) {
                const patternW = QuickTableUtils.GetWidth(context, cell.textPattern) + 3;
                const indexT = cell.formattedValue.toLowerCase().indexOf(cell.textPattern.toLowerCase());
                if (indexT !== -1) {
                    const textOffset = QuickTableUtils.GetWidth(context, cell.formattedValue.substring(0, indexT));
                    context.fillStyle = ThemeManager.colors.qtTextPatternColor;
                    context.fillRect(cellRectangle.X + 5 + textOffset, cellRectangle.Y + 2, patternW, cellRectangle.Height - 5);
                }
            }
            // cell data
            if ((_a = row.item) === null || _a === void 0 ? void 0 : _a.Disabled) {
                foreColor = ThemeManager.colors.qtDisabledRowTextColor;
            }
            const xPadding = this.getXPadding(curColumn.PRIVATE.index, context.textAlign);
            const yPadding = this.getYPadding(cellRectangle.Height, QuickTableUtils.GetTextHeight(context));
            const rectBrush = new SolidBrush(cellBackColor);
            context.FillRectWithRect(rectBrush, cellRectangle);
            const textBrush = new SolidBrush(foreColor);
            const backgroundBrush = new SolidBrush(cellBackColor);
            QuickTableUtils.DrawTextInRect(context, cell.formattedValue, this.tableFont, textBrush, cellRectangle.X, cellRectangle.Y, cellRectangle.Width, cellRectangle.Height, xPadding, yPadding, true, cell.textPattern, backgroundBrush);
        }
    }
    getXPadding(columnIndex, textAlign) {
        const column = this.columns[columnIndex];
        switch (textAlign) {
            case 'start':
            case 'left':
                return isValidNumber(column.leftPadding) ? column.leftPadding : this.cellLeftPadding;
            case 'end':
            case 'right':
                return isValidNumber(column.rightPadding) ? column.rightPadding : this.cellRightPadding;
            default:
                return 0;
        }
    }
    getYPadding(cellHeight, contentHeight) {
        return Math.floor((cellHeight - contentHeight) / 2);
    }
    drawButton(gr, defaultImage, hoveredImage, disabledImage, selectedImage, cell, cellX, cellY, cellW, cellH, lastMouseMoveX, lastMouseMoveY, rowSelected, rowHovered, tooltip) {
        var _a;
        const btnRectangle = this.getButtonRectangle(cellX, cellY, cellW, cellH);
        const style = this.getButtonStyle(defaultImage, hoveredImage, disabledImage, selectedImage, rowSelected, rowHovered, cell, btnRectangle, lastMouseMoveX, lastMouseMoveY);
        if (btnRectangle.Contains(lastMouseMoveX, lastMouseMoveY)) {
            const key = tooltip !== null && tooltip !== void 0 ? tooltip : (_a = cell === null || cell === void 0 ? void 0 : cell.QuickTableEditingInfo) === null || _a === void 0 ? void 0 : _a.TooltipKey;
            if (isValidString(key)) {
                this.OnShowTooltip.Raise(key);
            }
        }
        this.drawButtonStyle(gr, btnRectangle, style);
    }
    getButtonStyle(defaultImage, hoveredImage, disabledImage, selectedImage, rowSelected, rowHovered, cell, buttonRectangle, lastMouseMoveX, lastMouseMoveY) {
        let image = null;
        let backgroundBrush = null;
        let borderPen = null;
        const hover = buttonRectangle.Contains(lastMouseMoveX, lastMouseMoveY);
        if (cell === null || cell === void 0 ? void 0 : cell.ReadOnly) {
            image = disabledImage.image;
            backgroundBrush = new SolidBrush(ThemeManager.colors.tableBtnBackgroundDisabled);
            borderPen = new Pen(ThemeManager.colors.tableBtnBorderDisabled);
        }
        else if (hover) {
            if (rowSelected) {
                image = hoveredImage.image;
                backgroundBrush = new SolidBrush(ThemeManager.colors.tableBtnBackgroundHoveredInSelectedRow);
                borderPen = new Pen(ThemeManager.colors.tableBtnBorderHoveredInSelectedRow);
            }
            else if (rowHovered) {
                image = selectedImage.image;
                backgroundBrush = new SolidBrush(ThemeManager.colors.tableBtnBackgroundHoveredInHoveredRow);
                borderPen = new Pen(ThemeManager.colors.tableBtnBorderHoveredInHoveredRow);
            }
            else {
                image = selectedImage.image;
                backgroundBrush = new SolidBrush(ThemeManager.colors.tableBtnBackgroundHovered);
                borderPen = new Pen(ThemeManager.colors.tableBtnBorderHovered);
            }
        }
        else if (rowSelected) {
            image = selectedImage.image;
            backgroundBrush = new SolidBrush(ThemeManager.colors.tableBtnBackgroundInSelectedRow);
            borderPen = new Pen(ThemeManager.colors.tableBtnBorderInSelectedRow);
        }
        else if (rowHovered) {
            image = hoveredImage.image;
            backgroundBrush = new SolidBrush(ThemeManager.colors.tableBtnBackgroundInHoveredRow);
            borderPen = new Pen(ThemeManager.colors.tableBtnBorderInHoveredRow);
        }
        else {
            image = defaultImage.image;
            backgroundBrush = new SolidBrush(ThemeManager.colors.tableBtnBackground);
            borderPen = new Pen(ThemeManager.colors.tableBtnBorderRegular);
        }
        return { image, backgroundBrush, borderPen };
    }
    drawButtonStyle(gr, rectangle, style) {
        const btnPadding = QuickTable.BUTTON_PADDING;
        gr.RoundRectangle(rectangle, 4, style.backgroundBrush, style.borderPen);
        gr.DrawCenteredImage(style.image, rectangle, rectangle.Width - btnPadding * 2, rectangle.Height - btnPadding * 2);
    }
    getButtonRectangle(cellX, cellY, cellW, cellH, btnSize) {
        const buttonSize = btnSize !== null && btnSize !== void 0 ? btnSize : Math.round(this.rowHeight * 0.75);
        const x = Math.round(cellX + cellW / 2 - buttonSize / 2);
        const y = Math.round(cellY + cellH / 2 - buttonSize / 2);
        const btnRectangle = new Rectangle(x, y, buttonSize, buttonSize);
        return btnRectangle;
    }
    onDisplayModeChanged() {
        this.updateScrollElementsCount(true);
        this.OnDisplayModeChanged.Raise();
    }
    onScrollElementsCountChanged() {
        this.eventEmitter.emit('scrollElementsCountChanged');
    }
    getColumnTextAlign(column) {
        if (!isNullOrUndefined(this.CustomTextAlign))
            return this.CustomTextAlign;
        switch (column.alignment) {
            case QTAlignment.Right:
                return 'end';
            case QTAlignment.Center:
                return 'center';
            default:
                return 'start';
        }
    }
    getCellForegroundColor(row, cell, isSelected, isHovered, columnIndex) {
        var _a;
        if (isSelected && !isNullOrUndefined(this.selectedForeColor))
            return this.selectedForeColor;
        if (isHovered && !isNullOrUndefined(this.hoveredForeColor))
            return this.hoveredForeColor;
        if (cell.isError)
            return isHovered
                ? ThemeManager.colors.quickTableErrorForegroundHovered
                : ThemeManager.colors.quickTableErrorForeground;
        if (isValidString(cell.ForeColor))
            return cell.ForeColor;
        if (isValidString(row.ForeColor))
            return row.ForeColor;
        if (!isNullOrUndefined(columnIndex)) {
            const cellForeground = (_a = row.item) === null || _a === void 0 ? void 0 : _a.getQuickTableCellForeground(columnIndex);
            if (isValidString(cellForeground))
                return cellForeground;
        }
        return this.ForeColor;
    }
    recalculateGroupRows() {
        const groupRows = Object.values(this.groupStorageDict);
        for (const groupRow of groupRows) {
            groupRow.recalculateAggregates(this.ShowTotals);
        }
    }
    updateCursor(hitTestInfo) {
        this.currentCursor = Cursors.Default;
        if (this.movingType === QuickTableMovingType.ColumnResize) {
            this.currentCursor = Cursors.VSplit;
            return;
        }
        if (this.columnResizeAllowed &&
            (hitTestInfo.zone === QuickTableZone.HeaderGrid ||
                hitTestInfo.zone === QuickTableZone.VerticalResizeSeparator) &&
            hitTestInfo.columnIndex !== -1 &&
            !hitTestInfo.lastVisibleSortedColumn) {
            this.currentCursor = Cursors.VSplit;
            return;
        }
        const hoveredRow = this.rowsArray[hitTestInfo.rowIndex];
        if (!isNullOrUndefined(hoveredRow) && hoveredRow.isGroupRow) {
            this.currentCursor = Cursors.Hand;
            return;
        }
        this.updateCursorForCellControl(hitTestInfo);
    }
    updateCursorForCellControl(hitTestInfo) {
        var _a;
        const row = this.getTableRowFromHitTestInfo(hitTestInfo);
        const cell = this.getCellFromHitTestInfo(hitTestInfo);
        const column = this.sortedColumns[hitTestInfo.columnIndex];
        if (hitTestInfo.zone === QuickTableZone.Header) {
            this.currentCursor = Cursors.Hand;
            return;
        }
        if (isNullOrUndefined(cell === null || cell === void 0 ? void 0 : cell.QuickTableEditingInfo) || cell.ReadOnly || !cell.QuickTableEditingInfo.IsEditable) {
            this.currentCursor = Cursors.Default;
            return;
        }
        switch (cell.QuickTableEditingInfo.ControlType) {
            case DynProperty.INFO_PICTURE:
            case DynProperty.ORDER_BUTTON:
            case DynProperty.TRADE_BUTTON:
            case DynProperty.CLOSE_BUTTON:
            case DynProperty.VISIBILITY_BUTTON:
            case DynProperty.SETTINGS_BUTTON:
            case DynProperty.SHOW_ON_CHART_BUTTON:
            case DynProperty.ITCHART_ADVANCED:
            case DynProperty.SELL_ASSET_BUTTON:
            case DynProperty.PLACE_BUTTON:
            case DynProperty.CLOSE_VIA_OE_BUTTON:
            case DynProperty.BOOLEAN: {
                const pr = column.PRIVATE;
                const row = this.rowsArray[hitTestInfo.rowIndex];
                const cellW = pr.drawingWidth;
                const cellX = pr.drawingX;
                const cellH = row.displayRectangle.Height;
                const cellY = row.displayRectangle.Y;
                const btnRect = this.getButtonRectangle(cellX, cellY, cellW, cellH);
                if (btnRect.Contains(hitTestInfo.x, hitTestInfo.y)) {
                    this.currentCursor = Cursors.Hand;
                }
                break;
            }
            case DynProperty.DOUBLE:
            case DynProperty.COLOR:
            case DynProperty.COMBOBOX:
            case DynProperty.COMBOBOX_COMBOITEM_TIF:
            case DynProperty.INSTRUMENT:
            case DynProperty.ACCOUNT: {
                this.currentCursor = Cursors.Hand;
                break;
            }
            default:
                if (((_a = row === null || row === void 0 ? void 0 : row.item) === null || _a === void 0 ? void 0 : _a.isAllowEdit(hitTestInfo.columnIndex)) === true) {
                    this.currentCursor = Cursors.Hand;
                }
                break;
        }
    }
    getCellBackgroundColor(row, cell, rowHovered, rowSelected, rowIndex) {
        if (rowSelected) {
            return rowHovered ? this.selectedHoveredBackColor : this.selectedBackColor;
        }
        else if (rowHovered) {
            return cell.isError ? ThemeManager.colors.quickTableErrorBackgroundHovered : this.hoveredBackColor;
        }
        else if (cell.isError) {
            return ThemeManager.colors.quickTableErrorBackground;
        }
        else if (isValidString(cell.BackColor)) {
            return cell.BackColor;
        }
        else if (row.BackColor !== null) {
            return row.BackColor;
        }
        else if (rowIndex % 2 === 0) {
            return this.secondRowBackColor;
        }
        return this.rowBackColor;
    }
}
QuickTable.BUTTON_PADDING = 4;
QuickTable.CHECKBOX_SIZE = 16;
QuickTable.ResizeColumnsAreaPixel = 3;
QuickTable.MouseOffset = 5;
export var QuickTableZone;
(function (QuickTableZone) {
    QuickTableZone["Cell"] = "Cell";
    QuickTableZone["Header"] = "Header";
    QuickTableZone["Grid"] = "Grid";
    QuickTableZone["Table"] = "Table";
    QuickTableZone["VerticalResizeSeparator"] = "VerticalResizeSeparator";
    QuickTableZone["VerticalScroll"] = "VerticalScroll";
    QuickTableZone["VerticalScrollActiveZone"] = "VerticalScrollActiveZone";
    QuickTableZone["HeaderGrid"] = "HeaderGrid";
    QuickTableZone["TopScrollButton"] = "TopScrollButton";
    QuickTableZone["BottomScrollButton"] = "BottomScrollButton";
})(QuickTableZone || (QuickTableZone = {}));
export var QuickTableMovingType;
(function (QuickTableMovingType) {
    QuickTableMovingType["VerticalScroll"] = "VerticalScroll";
    QuickTableMovingType["HorizontalScroll"] = "HorizontalScroll";
    QuickTableMovingType["ColumnResize"] = "ColumnResize";
    QuickTableMovingType["ColumnMoving"] = "ColumnMoving";
    QuickTableMovingType["None"] = "None";
})(QuickTableMovingType || (QuickTableMovingType = {}));
export var GridVisibilityType;
(function (GridVisibilityType) {
    GridVisibilityType[GridVisibilityType["None"] = 0] = "None";
    GridVisibilityType[GridVisibilityType["Vertical"] = 1] = "Vertical";
    GridVisibilityType[GridVisibilityType["Horizontal"] = 2] = "Horizontal";
    GridVisibilityType[GridVisibilityType["Both"] = 3] = "Both";
})(GridVisibilityType || (GridVisibilityType = {}));
export class QuickTableDrawingCellEventArgs {
    constructor(table, row, columnIndex, cell, curFont, foreColor, backColor, x, y, width, height, rowHovered, lastMouseMoveX, lastMouseMoveY) {
        this.parentRowIsHovered = false;
        this.table = table;
        this.row = row;
        this.columnIndex = columnIndex;
        this.cell = cell;
        this.font = curFont;
        this.ForeColor = foreColor;
        this.BackColor = backColor;
        this.x = x;
        this.y = y;
        this.width = width;
        this.height = height;
        this.parentRowIsHovered = rowHovered;
        this.lastMouseMoveX = lastMouseMoveX;
        this.lastMouseMoveY = lastMouseMoveY;
        this.handled = false;
    }
}
export class QuickTableMouseEventArgs {
    constructor(table) {
        this.table = table;
        this.needRedraw = false;
    }
}
export class QuickTableMouseMoveEventArgs extends QuickTableMouseEventArgs {
    constructor(table, row, columnIndex, cell, lastMouseMoveX, lastMouseMoveY) {
        super(table);
        this.row = row;
        this.columnIndex = columnIndex;
        this.cell = cell;
        this.lastMouseMoveX = lastMouseMoveX;
        this.lastMouseMoveY = lastMouseMoveY;
        this.needRedraw = false;
        this.isTooltipHandled = false;
    }
}
